import { TextSelection, PluginKey, NodeSelection, Plugin } from 'prosemirror-state';
import 'prosemirror-commands';
import 'prosemirror-inputrules';
import { Slice, Fragment } from 'prosemirror-model';
import { NodeExtension } from '../../../core/index.js';
import { DecorationSet, Decoration } from 'prosemirror-view';
import { v4 } from 'uuid';
import React, { useState, useCallback, useRef, useEffect, useMemo, Component } from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import { createInstance } from '@sailor/i18n-web';
import ReactDOM from 'react-dom';

/**
 * 统一样式前缀
 */ var CLASSNAME_PREFIX = "ct";
/**
 * 零宽度空格
 */ var ZERO_WIDTH_SPACE = "​";

function getInsertBlockNodes(state, node) {
    var schema = state.schema, selection = state.selection, doc = state.doc;
    var paragraph = schema.nodes.paragraph;
    var $from = selection.$from, $to = selection.$to, to = selection.to;
    var afterNode = $to.nodeAfter;
    var fromNodeGrandParent = $from.node(-1);
    var fromNodeParent = $from.node();
    // 光标在文档尾部
    if (!afterNode) {
        var nodes = [
            node,
            paragraph.create()
        ];
        // 是否在文档尾部后单元格尾部
        var isInTail = to === doc.content.size - 1 || (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;
        if (isInTail) return nodes;
        // 后面一行是否为文本块节点（即有内联内容的块级节点，schema中定义content为inline或者text的节点例如段落、标题, ）
        var nextNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;
        if (nextNode && !nextNode.isTextblock && !nextNode.isText) {
            // 后面一行为非文本块节点
            return nodes;
        }
    }
    return [
        node
    ];
}
var isParagraph = function(node) {
    var _node_type;
    return (node === null || node === void 0 ? void 0 : (_node_type = node.type) === null || _node_type === void 0 ? void 0 : _node_type.name) === "paragraph";
};
// 插入块处理
function insertBlock(state, node) {
    var options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        selection: null,
        cursorMove: true
    };
    if (!node.isBlock) {
        throw new Error("必须传入 block node");
    }
    var tr = state.tr, schema = state.schema;
    var selection = options.selection || state.selection;
    var cursorMove = options.cursorMove;
    var _schema_nodes = schema.nodes, table = _schema_nodes.table, note = _schema_nodes.note;
    var from = selection.from, $from = selection.$from;
    // TODO: 为什么需要清除格式
    tr.setStoredMarks([]); // 清除格式
    var insertingNodes = getInsertBlockNodes(state, node);
    // TODO: selection.replace 并不是常用的函数，是否可以替换成 tr.replaceWith
    selection.replace(tr, new Slice(Fragment.from(insertingNodes), 0, 0));
    // transform 1.1 版本会在 replace 过程中增加多余的空行，此处进行判断并清理
    // transform 升级到 1.3 之后可以恢复此段代码的处理，不需要清理多余的空行
    // https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/20632944
    var step = tr.steps[0];
    if (step) {
        var slice = step === null || step === void 0 ? void 0 : step.slice;
        var content = (slice === null || slice === void 0 ? void 0 : slice.content).content;
        var size = content.length;
        // 末尾两个元素是否为 p 标签
        var removeParagraph = size > 2 && isParagraph(content[size - 1]) && isParagraph(content[size - 2]);
        if (removeParagraph) {
            // 如果替换时最后两个node都是 paragraph，说明有多余的空行，需要删除最后一个空行
            var lastPos = tr.mapping.map(step.to);
            tr.delete(lastPos - 2, lastPos);
        }
    }
    tr.setSelection(new TextSelection(tr.doc.resolve(tr.mapping.map(state.selection.from)), tr.doc.resolve(tr.mapping.map(state.selection.to))));
    // TODO:下面这段代码优化
    // 移动光标位置
    if (cursorMove) {
        var nodeBefore = $from.nodeBefore; // 光标前相邻节点
        var offset = nodeBefore ? 2 : 0;
        if (node.type.isLeaf) {
            offset += 1;
        } else if (node.type === note) {
            offset += 4;
        } else if (node.type === table) {
            offset += 3;
        }
        var newPos = selection.constructor.name === "GapCursor" ? from : from + offset;
        tr.setSelection(TextSelection.create(tr.doc, newPos));
    }
    return {
        transaction: tr
    };
}
var classNamePrefix = function(className) {
    var prefix = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : CLASSNAME_PREFIX;
    return "".concat(prefix, "-").concat(className);
};
/**
 * 格式化文件大小(File.size)，加上单位
 */ function formatFileSize(origin) {
    if (origin >= 1024 * 1024) {
        return "".concat((origin / (1024 * 1024)).toFixed(2), "MB");
    }
    if (origin >= 1024) {
        return "".concat((origin / 1024).toFixed(2), "KB");
    }
    return "".concat(origin || 0, "B");
}
/**
 *格式化时间字符串
 * @param time
 * @returns HH:MM格式字符串
 */ var formatTime = function(time) {
    var minute = parseInt("".concat(time / 60), 10);
    var second = parseInt("".concat(time % 60), 10);
    return "".concat(minute < 10 ? "0".concat(minute) : minute, ":").concat(second < 10 ? "0".concat(second) : second);
};

var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
// 类型是否可以插入富文本
var canInsertTypes = function(state) {
    var parentNode = state.selection.$from.parent;
    var textType = [
        "status",
        "code_block",
        "note_title",
        "collapse_title"
    ];
    return !textType.includes(parentNode.type.name);
};

function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
var optionsMerge = function() {
    var target = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {}, source = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    var res = _objectSpread({}, target, source);
    if (source.state) {
        res.state = optionsMerge(target.state, source.state);
    }
    if (source.props) {
        res.props = optionsMerge(target.props, source.props);
    }
    return res;
};
function UploaderOption(pluginOption, param) {
    var createUploadMask = param.createUploadMask;
    var uploaderOption = {
        state: {
            init: function init() {
                return DecorationSet.empty;
            },
            apply: function apply(tr, set) {
                // Adjust decoration positions to changes made by the transaction
                set = set.map(tr.mapping, tr.doc);
                // See if the transaction adds or removes any placeholders
                var action = tr.getMeta(this);
                if (action && action.add) {
                    var widget = createUploadMask(action.add, action.add.id);
                    var deco = Decoration.widget(action.add.pos, widget, {
                        id: action.add.id,
                        ignoreSelection: true
                    });
                    set = set.add(tr.doc, [
                        deco
                    ]);
                } else if (action && action.remove) {
                    set = set.remove(set.find(null, null, function(spec) {
                        return spec.id === action.remove.id;
                    }));
                }
                return set;
            }
        },
        props: {
            decorations: function decorations(state) {
                return this.getState(state);
            }
        }
    };
    return optionsMerge(pluginOption, uploaderOption);
}

/**
 * 插入视频
 * @return {boolean} 是否插入成功
 */ var insertVideo = function(attrs) {
    return function(state, dispatch, view, pos) {
        var schema = state.schema;
        var transaction = insertBlock(view.state, schema.nodes.video.create(attrs), {
            selection: new TextSelection(view.state.doc.resolve(pos)),
            cursorMove: false
        }).transaction;
        dispatch(transaction);
        view.focus();
        return true;
    };
};

var AFTER_INSERT = "after_insert";

var pluginKey = new PluginKey("video");

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
var __generator = window && window.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return(g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g);
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
/**
 * 参考自官网Demo：https://prosemirror.net/examples/upload/
 */ function findPlaceholder(view, id) {
    var decos = pluginKey.getState(view.state);
    var found = decos.find(null, null, function(spec) {
        return spec.id === id;
    });
    if (found.length) {
        return {
            pos: found[0].from,
            node: found[0].type.toDOM
        };
    }
    return null;
}
var onUpload = function(param) {
    var view = param.view, provider = param.provider, idMap = param.idMap, pos = param.pos, eventEmitter = param.eventEmitter;
    var getComponent = function(id) {
        return idMap[id];
    };
    return function() {
        var _ref = _asyncToGenerator(function(file) {
            var id, ret, attrs, tr, onProgress, data, comp, placeholder, e, placeholder1, comp1;
            return __generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        id = v4();
                        if (!provider.beforeUpload) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            provider.beforeUpload(file, id)
                        ];
                    case 1:
                        ret = _state.sent();
                        // 只有当校验函数显示返回 false 时才表示校验不通过
                        if (ret === false) {
                            view.focus();
                            return [
                                2
                            ];
                        }
                        _state.label = 2;
                    case 2:
                        attrs = {
                            name: file.name
                        };
                        tr = view.state.tr;
                        if (typeof pos !== "number") pos = tr.selection.from;
                        if (!tr.selection.empty) {
                            tr.deleteSelection();
                        }
                        tr.setMeta(pluginKey, {
                            add: {
                                id: id,
                                pos: pos,
                                file: file
                            }
                        });
                        tr.insertText(ZERO_WIDTH_SPACE, pos); // 为了能够在placeholder后输入文字
                        view.dispatch(tr);
                        eventEmitter.emit(AFTER_INSERT, {
                            braceWidth: 540,
                            pos: pos
                        }); // 如果在表格中，更新列宽
                        onProgress = function(param) {
                            var loaded = param.loaded, total = param.total;
                            var placeholder = findPlaceholder(view, id);
                            if (!placeholder) return;
                            var percent = Math.ceil(total ? loaded / total * 99 : 0);
                            var comp = getComponent(id);
                            comp === null || comp === void 0 ? void 0 : comp.setProgress(percent);
                            comp === null || comp === void 0 ? void 0 : comp.setLoaded(loaded);
                        };
                        _state.label = 3;
                    case 3:
                        _state.trys.push([
                            3,
                            7,
                            ,
                            8
                        ]);
                        return [
                            4,
                            provider.upload(file, onProgress)
                        ];
                    case 4:
                        data = _state.sent();
                        comp = getComponent(id);
                        comp === null || comp === void 0 ? void 0 : comp.setUploadDone(true);
                        if (!provider.videoTranscode) return [
                            3,
                            6
                        ];
                        return [
                            4,
                            provider.videoTranscode(data, file)
                        ];
                    case 5:
                        _state.sent();
                        _state.label = 6;
                    case 6:
                        comp === null || comp === void 0 ? void 0 : comp.setTranscodeDone(true);
                        placeholder = findPlaceholder(view, id);
                        if (!placeholder) return [
                            2
                        ];
                        data.size = formatFileSize(data.size);
                        // 删除 state 中保存的 placeholder
                        view.dispatch(view.state.tr.replaceWith(placeholder.pos, placeholder.pos + 1, []).setMeta(pluginKey, {
                            remove: {
                                id: id
                            }
                        }));
                        // 插入视频宏
                        insertVideo(_objectSpread$1({}, attrs, data))(view.state, view.dispatch, view, placeholder.pos);
                        delete idMap[id];
                        return [
                            3,
                            8
                        ];
                    case 7:
                        e = _state.sent();
                        console.error("上传失败", e);
                        placeholder1 = findPlaceholder(view, id);
                        if (!placeholder1) return [
                            2
                        ];
                        comp1 = getComponent(id);
                        comp1.onRetry = function() {
                            view.dispatch(view.state.tr.setMeta(pluginKey, {
                                remove: {
                                    id: id
                                }
                            }));
                            onUpload({
                                view: view,
                                provider: provider,
                                idMap: idMap,
                                pos: placeholder1.pos,
                                eventEmitter: eventEmitter
                            })(file);
                        };
                        comp1 === null || comp1 === void 0 ? void 0 : comp1.setHasError(true);
                        return [
                            3,
                            8
                        ];
                    case 8:
                        return [
                            2
                        ];
                }
            });
        });
        return function(file) {
            return _ref.apply(this, arguments);
        };
    }();
};
var selectFile = function(param) {
    var accept = param.accept, _param_multiple = param.multiple, multiple = _param_multiple === void 0 ? true : _param_multiple;
    return new Promise(function(resolve) {
        var input = document.createElement("input");
        input.setAttribute("type", "file");
        if (multiple) {
            input.setAttribute("multiple", "multiple");
        }
        input.setAttribute("accept", accept);
        input.onchange = function(e) {
            var files = e.target.files;
            input.remove();
            resolve(files);
        };
        input.click();
    });
};

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterableToArrayLimit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray(arr, i) {
    return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function useForceUpdate() {
    var _useState = _slicedToArray(useState(0), 2), setTick = _useState[1];
    var update = useCallback(function() {
        setTick(function(tick) {
            return tick + 1;
        });
    }, []);
    return update;
}

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = '.ct-editor-container .ct-node-view-dom.ProseMirror-selectednode .ct-pc-resizer{display:block;outline:1px dashed #9b9b9b}.ct-editor-container .ct-pc-resizer{display:none;position:absolute;left:0;right:0;top:0;bottom:0;z-index:1;pointer-events:none;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.ct-editor-container .ct-pc-resizer-point{position:absolute;height:20px;width:20px;pointer-events:all}.ct-editor-container .ct-pc-resizer-point:after{content:"";position:absolute;left:4px;top:4px;height:12px;width:12px;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 2px 0;background-color:#333}.ct-editor-container .ct-pc-resizer-point.lt{cursor:pointer;left:-10px;top:-10px;cursor:nwse-resize}.ct-editor-container .ct-pc-resizer-point.rt{cursor:pointer;right:-10px;top:-10px;cursor:nesw-resize}.ct-editor-container .ct-pc-resizer-point.lb{cursor:pointer;left:-10px;bottom:-10px;cursor:nesw-resize}.ct-editor-container .ct-pc-resizer-point.rb{cursor:pointer;right:-10px;bottom:-10px;cursor:nwse-resize}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point{height:30px;width:30px}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point:after{left:9px;top:9px}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point.lt{cursor:pointer;left:-15px;top:-15px;cursor:nwse-resize}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point.rt{cursor:pointer;right:-15px;top:-15px;cursor:nesw-resize}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point.lb{cursor:pointer;left:-15px;bottom:-15px;cursor:nesw-resize}.ct-editor-container .ct-pc-resizer .large-hot-area.ct-pc-resizer-point.rb{cursor:pointer;right:-15px;bottom:-15px;cursor:nwse-resize}.ct-editor-container .ct-pc-resizer-info{position:absolute;height:20px;line-height:20px;padding:0 4px;text-align:center;background:rgba(0,0,0,.72);border-radius:4px;font-size:12px;color:#fff}.ct-editor-container .ct-pc-resizer-info.lt{left:9px;top:9px}.ct-editor-container .ct-pc-resizer-info.lt.out{left:-60px;top:0}.ct-editor-container .ct-pc-resizer-info.lb{left:9px;bottom:9px}.ct-editor-container .ct-pc-resizer-info.lb.out{left:-60px;bottom:0}.ct-editor-container .ct-pc-resizer-info.rt{right:9px;top:9px}.ct-editor-container .ct-pc-resizer-info.rt.out{right:-60px;top:0}.ct-editor-container .ct-pc-resizer-info.rb{right:9px;bottom:9px}.ct-editor-container .ct-pc-resizer-info.rb.out{right:-60px;bottom:0}.ct-editor-container .ct-pc-resizer .auto-hide{display:none}.ct-editor-container .ct-pc-resizer-point:hover~.auto-hide{display:block}';
styleInject(css_248z);

function _arrayLikeToArray$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithHoles$1(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterableToArrayLimit$1(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _nonIterableRest$1() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _slicedToArray$1(arr, i) {
    return _arrayWithHoles$1(arr) || _iterableToArrayLimit$1(arr, i) || _unsupportedIterableToArray$1(arr, i) || _nonIterableRest$1();
}
function _unsupportedIterableToArray$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray$1(o, minLen);
}
var Resizer = function(param) {
    var $container = param.$container, view = param.view, getPos = param.getPos, initialWidth = param.initialWidth, initialHeight = param.initialHeight, _param_fixedRatio = param.fixedRatio, fixedRatio = _param_fixedRatio === void 0 ? true : _param_fixedRatio, _param_autoHeight = param.autoHeight, autoHeight = _param_autoHeight === void 0 ? false : _param_autoHeight, _param_hideInfo = param.hideInfo, hideInfo = _param_hideInfo === void 0 ? false : _param_hideInfo, _param_limit = param.limit, limit = _param_limit === void 0 ? {} : _param_limit, onResizeStart = param.onResizeStart, onResizeEnd = param.onResizeEnd;
    if (!$container || !getPos()) return null;
    var limitRef = useRef({});
    limitRef.current.minWidth = limit.minWidth || 20;
    limitRef.current.minHeight = limit.minHeight || 20;
    limitRef.current.maxHeight = limit.maxHeight || Infinity;
    var _useState = _slicedToArray$1(useState("rb"), 2), infoPosition = _useState[0], setInfoPosition = _useState[1];
    var wrapperRef = useRef();
    // 获取最大宽度
    var pos = getPos();
    var doc = view.state.doc;
    // 获取父级block节点
    var blockNode = findParentNodeClosestToPos(doc.resolve(pos), function(n) {
        return n.isBlock;
    });
    var $blockNode = view.nodeDOM((blockNode === null || blockNode === void 0 ? void 0 : blockNode.pos) || 0);
    if (!$blockNode) {
        return null;
    }
    limitRef.current.maxWidth = limit.maxWidth || $blockNode.clientWidth;
    var ratio = initialHeight / initialWidth;
    if (fixedRatio) {
        limitRef.current.minHeight = Math.max(limitRef.current.minHeight, limitRef.current.minWidth * ratio);
        limitRef.current.minWidth = Math.max(limitRef.current.minWidth, limitRef.current.minHeight / ratio);
        limitRef.current.maxHeight = limitRef.current.maxWidth * ratio;
    }
    limitRef.current.minWidth = Math.min(limitRef.current.minWidth, limitRef.current.maxWidth);
    limitRef.current.minHeight = Math.min(limitRef.current.minHeight, limitRef.current.maxHeight);
    var startX = useRef(0);
    var startY = useRef(0);
    var isReverseX = useRef(false);
    var isReverseY = useRef(false);
    var initialWidthRef = useRef(0);
    var initialHeightRef = useRef(0);
    initialWidthRef.current = initialWidth;
    initialHeightRef.current = initialHeight;
    var currentSize = useRef({
        width: initialWidth,
        height: initialHeight
    });
    var update = useForceUpdate();
    var limitSize = function() {
        var _currentSize_current = currentSize.current, width = _currentSize_current.width, height = _currentSize_current.height;
        if (width >= limitRef.current.maxWidth) {
            currentSize.current.width = limitRef.current.maxWidth;
        } else if (width <= limitRef.current.minWidth) {
            currentSize.current.width = limitRef.current.minWidth;
        }
        if (height >= limitRef.current.maxHeight) {
            currentSize.current.height = limitRef.current.maxHeight;
        } else if (height <= limitRef.current.minHeight) {
            currentSize.current.height = limitRef.current.minHeight;
        }
    };
    useEffect(function() {
        var onMouseDown = function(e) {
            var classList = e.target && e.target.classList;
            if (!classList) {
                return;
            }
            var posList = [
                "lt",
                "rt",
                "lb",
                "rb"
            ];
            var posName = posList.find(function(item) {
                return classList.contains(item);
            });
            if (posName) {
                e.stopPropagation();
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                handleMouseDown(e, posName);
            }
        };
        var wrapper = wrapperRef.current;
        wrapper.addEventListener("mousedown", onMouseDown, false);
        return function() {
            wrapper.removeEventListener("mousedown", onMouseDown, false);
        };
    });
    useEffect(function() {
        currentSize.current.width = initialWidth;
        currentSize.current.height = initialHeight;
        if (!autoHeight) {
            // 防止容器节点大小没有同步更新
            $container.style.height = "".concat(currentSize.current.height, "px");
        }
        update();
    }, [
        initialWidth
    ]);
    var handleMouseMove = useCallback(function(e) {
        e.preventDefault();
        e.stopPropagation();
        var diffX = isReverseX.current ? startX.current - e.clientX : e.clientX - startX.current;
        var diffY = isReverseY.current ? startY.current - e.clientY : e.clientY - startY.current;
        currentSize.current.width = initialWidthRef.current + diffX;
        currentSize.current.height = initialHeightRef.current + diffY;
        if (fixedRatio) {
            currentSize.current.height = currentSize.current.width * ratio;
        }
        limitSize();
        currentSize.current.width = Math.round(currentSize.current.width);
        currentSize.current.height = Math.round(currentSize.current.height);
        $container.style.width = "".concat(currentSize.current.width, "px");
        if (!autoHeight) {
            $container.style.height = "".concat(currentSize.current.height, "px");
        }
        // 更新尺寸大小的显示
        update();
    }, []);
    var handleMouseUp = useCallback(function() {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        onResizeEnd({
            width: currentSize.current.width,
            height: currentSize.current.height
        });
        if (!autoHeight) {
            $container.style.height = "".concat(currentSize.current.height, "px");
        }
        $container.style.width = "".concat(currentSize.current.width, "px");
    }, []);
    var handleMouseDown = function(e, position) {
        isReverseX.current = position === "lt" || position === "lb";
        isReverseY.current = position === "lt" || position === "lr";
        startX.current = e.clientX;
        startY.current = e.clientY;
        setInfoPosition(position);
        if (onResizeStart) {
            onResizeStart();
        }
        document.addEventListener("mousemove", handleMouseMove, {
            passive: false
        });
        document.addEventListener("mouseup", handleMouseUp);
    };
    // 当图片尺寸较大时，扩大热区
    var largeHotArea = useMemo(function() {
        return currentSize.current.height >= 30 && currentSize.current.width >= 30;
    }, [
        currentSize.current.height,
        currentSize.current.width
    ]);
    var resizerPrefix = classNamePrefix("pc-resizer");
    var pointerCls = "".concat(resizerPrefix, "-point");
    return /*#__PURE__*/ React.createElement("div", {
        className: resizerPrefix,
        ref: wrapperRef
    }, /*#__PURE__*/ React.createElement("span", {
        className: "".concat(pointerCls, " lt ").concat(largeHotArea && "large-hot-area")
    }), /*#__PURE__*/ React.createElement("span", {
        className: "".concat(pointerCls, " rt ").concat(largeHotArea && "large-hot-area")
    }), /*#__PURE__*/ React.createElement("span", {
        className: "".concat(pointerCls, " lb ").concat(largeHotArea && "large-hot-area")
    }), /*#__PURE__*/ React.createElement("span", {
        className: "".concat(pointerCls, " rb ").concat(largeHotArea && "large-hot-area")
    }), /*#__PURE__*/ React.createElement("span", {
        className: "".concat(resizerPrefix, "-info ").concat(infoPosition, " ").concat((currentSize.current.width < 65 || currentSize.current.height < 30) && "out", " ").concat(hideInfo ? "auto-hide" : "")
    }, "".concat(currentSize.current.width, " x ").concat(currentSize.current.height)));
};

var img = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e6%92%ad%e6%94%be%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-471.000000%2c -558.000000)'%3e %3cg id='Group-18' transform='translate(471.000000%2c 558.000000)'%3e %3crect id='Rectangle-27' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cpath d='M15.6839276%2c10.5567733 L4.96564206%2c16.9107492 C4.66057577%2c17.0915976 4.26783668%2c16.9889051 4.08843476%2c16.6813793 C4.03053197%2c16.5821239 4%2c16.4690706 4%2c16.3539244 L4%2c3.64597255 C4%2c3.28921176 4.28689869%2c3 4.64080615%2c3 C4.7550314%2c3 4.86718053%2c3.03077813 4.96564206%2c3.08914775 L15.6839276%2c9.44312369 C15.9889939%2c9.62397201 16.090865%2c10.0198775 15.9114631%2c10.3274033 C15.8562054%2c10.4221245 15.7778912%2c10.5010701 15.6839276%2c10.5567733 Z' id='Triangle-2' fill='%236B6B6B'%3e%3c/path%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$1 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e6%9a%82%e5%81%9c%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-395.000000%2c -558.000000)'%3e %3cg id='Group-18-Copy' transform='translate(395.000000%2c 558.000000)'%3e %3crect id='Rectangle-27' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cpath d='M5%2c3 L7%2c3 C7.55228475%2c3 8%2c3.44771525 8%2c4 L8%2c16 C8%2c16.5522847 7.55228475%2c17 7%2c17 L5%2c17 C4.44771525%2c17 4%2c16.5522847 4%2c16 L4%2c4 C4%2c3.44771525 4.44771525%2c3 5%2c3 Z M13%2c3 L15%2c3 C15.5522847%2c3 16%2c3.44771525 16%2c4 L16%2c16 C16%2c16.5522847 15.5522847%2c17 15%2c17 L13%2c17 C12.4477153%2c17 12%2c16.5522847 12%2c16 L12%2c4 C12%2c3.44771525 12.4477153%2c3 13%2c3 Z' id='Combined-Shape' fill='%236B6B6B'%3e%3c/path%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$2 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e9%9f%b3%e9%87%8f%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-514.000000%2c -558.000000)'%3e %3cg id='Group-22' transform='translate(514.000000%2c 558.000000)'%3e %3crect id='Rectangle-30' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cg id='Group-20' transform='translate(2.000000%2c 4.000000)'%3e %3cg id='Group-21'%3e %3cpath d='M0%2c8.12082937 C0%2c8.6754175 0.442660332%2c9.125 0.998956561%2c9.125 L3%2c9.125 L6.19054031%2c11.4182009 C6.63759255%2c11.7395196 7%2c11.544239 7%2c10.9975267 L7%2c1.00247329 C7%2c0.448822582 6.63085938%2c0.265319824 6.19054031%2c0.58179915 L3%2c2.875 L0.998956561%2c2.875 C0.447248087%2c2.875 0%2c3.32448459 0%2c3.87917063 L0%2c8.12082937 Z' id='Fill-89' fill='%236B6B6B'%3e%3c/path%3e %3cpath d='M10%2c2 C11.3333333%2c3.06070964 12%2c4.39404297 12%2c6 C12%2c7.60595703 11.3333333%2c8.93929036 10%2c10' id='Path-12' stroke='%236B6B6B' stroke-width='1.5' stroke-linecap='round'%3e%3c/path%3e %3cpath d='M13%2c0 C15%2c1.3984375 16%2c3.3984375 16%2c6 C16%2c8.6015625 15%2c10.6015625 13%2c12' id='Path-12-Copy' stroke='%236B6B6B' stroke-width='1.5' stroke-linecap='round'%3e%3c/path%3e %3c/g%3e %3c/g%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$3 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e9%9d%99%e9%9f%b3%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-550.000000%2c -558.000000)'%3e %3cg id='Group-22-Copy' transform='translate(550.000000%2c 558.000000)'%3e %3crect id='Rectangle-30' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cg id='Group-20' transform='translate(4.000000%2c 4.000000)' fill='%236B6B6B'%3e %3cpath d='M9%2c1.72792206 L2.12273522%2c8.60518684 C2.04439515%2c8.46156167 2%2c8.29646112 2%2c8.12082937 L2%2c3.87917063 C2%2c3.32448459 2.44724809%2c2.875 2.99895656%2c2.875 L5%2c2.875 L8.19054031%2c0.58179915 C8.63085937%2c0.265319824 9%2c0.448822582 9%2c1.00247329 L9%2c1.72792206 Z M9%2c6.67766953 L9%2c10.9975267 C9%2c11.544239 8.63759255%2c11.7395196 8.19054031%2c11.4182009 L5.90337136%2c9.77429817 L9%2c6.67766953 Z M12.2582521%2c0.944543648 C12.5511454%2c1.23743687 12.5511454%2c1.7123106 12.2582521%2c2.00520382 L2.00520382%2c12.2582521 C1.7123106%2c12.5511454 1.23743687%2c12.5511454 0.944543648%2c12.2582521 C0.651650429%2c11.9653589 0.651650429%2c11.4904852 0.944543648%2c11.197592 L11.197592%2c0.944543648 C11.4904852%2c0.651650429 11.9653589%2c0.651650429 12.2582521%2c0.944543648 Z' id='Combined-Shape'%3e%3c/path%3e %3c/g%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$4 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e5%85%a8%e5%b1%8f%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-586.000000%2c -558.000000)'%3e %3cg id='Group-24' transform='translate(586.000000%2c 558.000000)'%3e %3crect id='Rectangle-30' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cpath d='M17.028246%2c3.75000027 C17.0282663%2c3.75227439 17.0282764%2c3.75455093 17.0282764%2c3.75682984 L17.0282764%2c9.25682984 C17.0282764%2c9.6710434 16.69249%2c10.0068298 16.2782764%2c10.0068298 C15.8640629%2c10.0068298 15.5282764%2c9.6710434 15.5282764%2c9.25682984 L15.5282764%2c5.55025253 L11.5803179%2c9.4982111 C11.2874247%2c9.79110432 10.8125509%2c9.79110432 10.5196577%2c9.4982111 C10.2267645%2c9.20531788 10.2267645%2c8.73044414 10.5196577%2c8.43755093 L14.4572086%2c4.5 L10.7782764%2c4.5 C10.3640629%2c4.5 10.0282764%2c4.16421356 10.0282764%2c3.75 C10.0282764%2c3.33578644 10.3640629%2c3 10.7782764%2c3 L16.2782764%2c3 C16.69249%2c3 17.0282764%2c3.33578644 17.0282764%2c3.75 Z M3.00003045%2c16.2499997 C3.00001016%2c16.2477256 3%2c16.2454491 3%2c16.2431702 L3%2c10.7431702 C3%2c10.3289566 3.33578644%2c9.99317016 3.75%2c9.99317016 C4.16421356%2c9.99317016 4.5%2c10.3289566 4.5%2c10.7431702 L4.5%2c14.4497475 L8.44795857%2c10.5017889 C8.74085178%2c10.2088957 9.21572552%2c10.2088957 9.50861874%2c10.5017889 C9.80151196%2c10.7946821 9.80151196%2c11.2695559 9.50861874%2c11.5624491 L5.57106781%2c15.5 L9.25%2c15.5 C9.66421356%2c15.5 10%2c15.8357864 10%2c16.25 C10%2c16.6642136 9.66421356%2c17 9.25%2c17 L3.75%2c17 C3.33578644%2c17 3%2c16.6642136 3%2c16.25 Z' id='Combined-Shape' fill='%236B6B6B'%3e%3c/path%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$5 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e7%bc%a9%e5%b0%8f%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='V1.2_Icon%e6%b1%87%e6%80%bb' transform='translate(-622.000000%2c -558.000000)'%3e %3cg id='Group-24-Copy' transform='translate(622.000000%2c 558.000000)'%3e %3crect id='Rectangle-30' stroke='%23979797' fill='%23D8D8D8' opacity='0' x='0.5' y='0.5' width='19' height='19'%3e%3c/rect%3e %3cpath d='M7.44974747%2c11.5068298 L3.77827644%2c11.5068298 C3.36406288%2c11.5068298 3.02827644%2c11.1710434 3.02827644%2c10.7568298 C3.02827644%2c10.3426163 3.36406288%2c10.0068298 3.77827644%2c10.0068298 L9.27827644%2c10.0068298 C9.69249001%2c10.0068298 10.0282764%2c10.3426163 10.0282764%2c10.7568298 C10.0282663%2c10.7591042 10.0282764%2c10.7613808 10.0282764%2c10.7636597 L10.0282764%2c16.2636597 C10.0282764%2c16.6778732 9.69249001%2c17.0136597 9.27827644%2c17.0136597 C8.86406288%2c17.0136597 8.52827644%2c16.6778732 8.52827644%2c16.2636597 L8.52827644%2c12.5496212 L4.59099026%2c16.4869074 C4.29809704%2c16.7798006 3.8232233%2c16.7798006 3.53033009%2c16.4869074 C3.23743687%2c16.1940142 3.23743687%2c15.7191404 3.53033009%2c15.4262472 L7.44974747%2c11.5068298 Z M11.5%2c7.46403847 L15.4372862%2c3.52675228 C15.7301794%2c3.23385906 16.2050531%2c3.23385906 16.4979464%2c3.52675228 C16.7908396%2c3.8196455 16.7908396%2c4.29451923 16.4979464%2c4.58741245 L12.578529%2c8.50682984 L16.25%2c8.50682984 C16.6642136%2c8.50682984 17%2c8.84261627 17%2c9.25682984 C17%2c9.6710434 16.6642136%2c10.0068298 16.25%2c10.0068298 L10.75%2c10.0068298 C10.3357864%2c10.0068298 10%2c9.6710434 10%2c9.25682984 C10.0000102%2c9.25455545 10%2c9.25227891 10%2c9.25 L10%2c3.75 C10%2c3.33578644 10.3357864%2c3 10.75%2c3 C11.1642136%2c3 11.5%2c3.33578644 11.5%2c3.75 L11.5%2c7.46403847 Z' id='Combined-Shape' fill='%236B6B6B'%3e%3c/path%3e %3c/g%3e %3cg id='Group-5' transform='translate(395.000000%2c 558.000000)'%3e%3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$6 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='14px' height='14px' viewBox='0 0 14 14' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 52.2 (67145) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3e%e4%b8%8b%e8%bd%bd copy%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='%e9%99%84%e4%bb%b6%e6%b7%bb%e5%8a%a0%e5%a4%8d%e5%88%b6%e9%93%be%e6%8e%a5' transform='translate(-369.000000%2c -555.000000)' fill='%23747272'%3e %3cg id='%e4%b8%8a%e4%bc%a0%e9%99%84%e4%bb%b6%e4%b8%ad-copy-6' transform='translate(56.000000%2c 534.000000)'%3e %3cg id='%e4%b8%8b%e8%bd%bd-copy' transform='translate(312.000000%2c 20.000000)'%3e %3cpath d='M8.81079102%2c8.49228232 L11.0575929%2c6.23659883 C11.367661%2c5.92438892 11.8514298%2c5.89865471 12.1342183%2c6.18304917 C12.4189783%2c6.46942638 12.3934758%2c6.95251315 12.0816605%2c7.26648227 L8.65828197%2c10.7033935 C8.57058276%2c10.8187868 8.45021995%2c10.9080019 8.31097304%2c10.9572592 C8.23162324%2c10.986943 8.14990291%2c11.0008681 8.06879562%2c10.9999582 C8.06613072%2c10.999986 8.0634625%2c11 8.06079102%2c11 C8.05811953%2c11 8.05545131%2c10.999986 8.05278641%2c10.9999582 C7.97167912%2c11.0008681 7.88995879%2c10.986943 7.81060899%2c10.9572592 C7.67136208%2c10.9080019 7.55099928%2c10.8187868 7.46330006%2c10.7033935 L4.03992152%2c7.26648227 C3.72810619%2c6.95251315 3.70260373%2c6.46942638 3.98736375%2c6.18304917 C4.27015222%2c5.89865471 4.75392098%2c5.92438892 5.06398917%2c6.23659883 L7.31079102%2c8.49228232 L7.31079102%2c1.75 C7.31079102%2c1.33578644 7.64657745%2c1 8.06079102%2c1 C8.47500458%2c1 8.81079102%2c1.33578644 8.81079102%2c1.75 L8.81079102%2c8.49228232 Z M15%2c11.0225156 L15%2c13 C15%2c14.1045695 14.1045695%2c15 13%2c15 L3%2c15 C1.8954305%2c15 1%2c14.1045695 1%2c13 L1%2c11 L1.00152941%2c11 C1.00051495%2c10.9840173 1%2c10.9678989 1%2c10.9516602 C1%2c10.5365365 1.33652425%2c10.2000122 1.75164795%2c10.2000122 C2.16677165%2c10.2000122 2.5032959%2c10.5365365 2.5032959%2c10.9516602 C2.5032959%2c10.9755555 2.50218087%2c10.9991903 2.5%2c11.0225156 L2.5%2c12.5 C2.5%2c13.0522847 2.94771525%2c13.5 3.5%2c13.5 L12.5%2c13.5 C13.0522847%2c13.5 13.5%2c13.0522847 13.5%2c12.5 L13.5%2c11 L13.5015294%2c11 C13.5005149%2c10.9840173 13.5%2c10.9678989 13.5%2c10.9516602 C13.5%2c10.5365365 13.8365242%2c10.2000122 14.2516479%2c10.2000122 C14.6667716%2c10.2000122 15.0032959%2c10.5365365 15.0032959%2c10.9516602 C15.0032959%2c10.9755555 15.0021809%2c10.9991903 15%2c11.0225156 Z' id='%e4%b8%8b%e8%bd%bd'%3e%3c/path%3e %3c/g%3e %3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var locale = "en";
var version = 16;
var cannot_search_user = "User not found, click space to complete input";
var load_fail_retry = "Failed to load, please try again later";
var pk_avatar = "avatar";
var mention_by_mis_name = "Can @user by name and MIS number";
var searching = "Searching...";
var pk_link_text = "Text";
var insert_blank = "Insert";
var pk_visit_link = "Access the link";
var pk_link_address = "Address";
var pk_link = "Link";
var cancel_blank = "Cancel";
var pk_link_change_tip = "Link text follows the Doc change";
var pk_edit_link = "Edit link";
var pk_link_text_tip = "Input the link text";
var pk_cancel_link = "Cancel link";
var save_blank = "Save";
var pk_link_address_tip = "Input the link address";
var border = "Border";
var pk_insert = "Insert";
var image_link = "Image Link";
var pk_retry = "Retry";
var pk_edit = "Edit";
var width = "Width";
var reupload = "Reupload";
var image_upload_fail1 = "Sorry, upload error!";
var right_address_tip = "Please input right address";
var image_upload_fail3 = "Otherwise, please click button below to retry.";
var image_upload_fail2 = "If the image is moved/pasted from outside, please download and reupload.";
var pk_confirm = "Confirm";
var input_html = "Enter HTML code.";
var formual = "Formual";
var pk_loading = "Loading...";
var click_to_expand = "Click to expand the content";
var pk_cancel = "Cancel";
var input_html_tip = "You can insert HTML code to embed corresponding content on a webpage.";
var input_latex = "Enter Latex code";
var insert_latex_tip = "You can insert Latex code to embed corresponding content within the page.";
var pk_title = "Title";
var wire_frame = "Wireframe";
var fill_in = "Fill";
var input_plantuml = "Enter PlantUML code.";
var border_style = "Border type";
var set_th_line = "Header Column";
var set_th_row = "Header Row";
var table_attr = "Table style";
var cancel_th_line = "Cancel Header Column";
var insert_line_right = "Right Insert Column";
var insert_row = "Delete Rows";
var split_cell = "Split cells";
var delete_line = "Delete Columns";
var pk_no = "No borders";
var input_markdown_tip = "You can insert Markdown code to embed corresponding content within the page.";
var confirm_blank = "Confirm";
var cancel_th_row = "Cancel Header Row";
var insert_row_above = "Insert Row Above";
var delete_table = "Delete";
var input_plantuml_tip = "You can insert PlantUML code to embed corresponding content within the page.";
var border_width = "Border width";
var input_markdown = "Enter Markdown code.";
var merge_cell = "Merge cells";
var set_line_num = "Show Row Number";
var border_color = "Border color";
var insert_line_left = "Left Insert Column";
var insert_row_below = "Insert Row Below";
var cancel_line_num = "Cancel Row Number";
var exit_data_lost_tip = "If you exit, any unsaved data will be lost.";
var if_exit_gantt = "Are you sure to exit the Gantt chart?";
var pk_gantt_loading = "Loading Gantt chart...";
var gantt_load_fail = "Gantt chart loading failed.";
var operate_error = "Invalid operation";
var gantt_keep_work_tip = "Please keep at least one task.";
var save_success = "Save successful";
var gantt_frame_load_fail = "Gantt chart framework failed to load.";
var pk_network_exception = "Network exception";
var save_fail = "Save failed";
var pk_saving = "Saving...";
var gantt_plugin_fail = "Gantt chart editing plugin failed to start.";
var gantt_editing_tip = "Editing Gantt Chart, please wait a moment.";
var pk_discard = "Drop";
var pk_zoom_in = "Zoom in";
var pk_zoom_out = "Zoom out";
var pk_use = "Use";
var draft_edit_tip = "There is a draft that was edited but not saved on {time}. Do you want to continue editing?";
var if_use_local_draft = "Do you use local drafts?";
var pk_load_fail = "Loading failed";
var pk_this_time = "Now";
var choose_time = "Select time";
var pk_hh_mm_ss = "hh:mm:ss A";
var pk_y_d = "MM-DD-YYYY";
var pk_y_d_ts = "MM-DD-YYYY hh:mm:ss A";
var pk_text = "Text";
var h1 = "Heading 1";
var h2 = "Heading 2";
var h3 = "Heading 3";
var h4 = "Heading 4";
var h5 = "Heading 5";
var h6 = "Heading 6";
var pk_catalog = "Catalog";
var page_view_load_fail_click = "Directory tree loading failed. Please click to reload.";
var page_tree_no_sub_doc = "No sub documents found in this directory";
var page_tree_no_perm = "No permission to view the directory tree.";
var pk_load_fail_retry = "Loading failed, please click to reload.";
var loading2 = "Loading";
var page_tree_deleted = "The directory tree has been deleted.";
var pk_pause = "Pause";
var pk_mute = "Mute";
var pk_untitled = "Untitled";
var pk_play = "Play";
var adjust_volume = "Adjust volume";
var download_fail_retry = "Download failed please retry";
var pk_download = "Download";
var get_file_fail = "Failed to get file";
var pk_network_fail = "Network connection failed";
var pk_upload_fail = "Upload failed";
var audio_not_support_tip = "This browser does not support the HTML5 audio tag.";
var pk_volume = "Volume";
var browser_not_support_tip = "This browser does not support the HTML5 video tag.";
var pk_zoom_in2 = "Zoom in";
var video_upload_tip = "Video is being transcoded, if the file is large, it may take a long time. Please wait patiently...";
var pk_video = "Video";
var pk_zoom_out2 = "Zoom out";
var space_update_fail_tip = "Space update acquisition failed. Click to retry.";
var pk_recent_update = "Recent Updates";
var show_more = "Show More";
var no_space_update = "No Updates in Space.";
var pk_restore = "Restore";
var en_parker = {
    locale: locale,
    version: version,
    cannot_search_user: cannot_search_user,
    load_fail_retry: load_fail_retry,
    pk_avatar: pk_avatar,
    mention_by_mis_name: mention_by_mis_name,
    searching: searching,
    pk_link_text: pk_link_text,
    insert_blank: insert_blank,
    pk_visit_link: pk_visit_link,
    pk_link_address: pk_link_address,
    pk_link: pk_link,
    cancel_blank: cancel_blank,
    pk_link_change_tip: pk_link_change_tip,
    pk_edit_link: pk_edit_link,
    pk_link_text_tip: pk_link_text_tip,
    pk_cancel_link: pk_cancel_link,
    save_blank: save_blank,
    pk_link_address_tip: pk_link_address_tip,
    border: border,
    pk_insert: pk_insert,
    image_link: image_link,
    pk_retry: pk_retry,
    pk_edit: pk_edit,
    width: width,
    reupload: reupload,
    image_upload_fail1: image_upload_fail1,
    right_address_tip: right_address_tip,
    image_upload_fail3: image_upload_fail3,
    image_upload_fail2: image_upload_fail2,
    pk_confirm: pk_confirm,
    input_html: input_html,
    formual: formual,
    pk_loading: pk_loading,
    click_to_expand: click_to_expand,
    pk_cancel: pk_cancel,
    input_html_tip: input_html_tip,
    input_latex: input_latex,
    insert_latex_tip: insert_latex_tip,
    pk_title: pk_title,
    wire_frame: wire_frame,
    fill_in: fill_in,
    input_plantuml: input_plantuml,
    border_style: border_style,
    set_th_line: set_th_line,
    set_th_row: set_th_row,
    table_attr: table_attr,
    cancel_th_line: cancel_th_line,
    insert_line_right: insert_line_right,
    insert_row: insert_row,
    split_cell: split_cell,
    delete_line: delete_line,
    pk_no: pk_no,
    input_markdown_tip: input_markdown_tip,
    confirm_blank: confirm_blank,
    cancel_th_row: cancel_th_row,
    insert_row_above: insert_row_above,
    delete_table: delete_table,
    input_plantuml_tip: input_plantuml_tip,
    border_width: border_width,
    input_markdown: input_markdown,
    merge_cell: merge_cell,
    set_line_num: set_line_num,
    border_color: border_color,
    insert_line_left: insert_line_left,
    insert_row_below: insert_row_below,
    cancel_line_num: cancel_line_num,
    exit_data_lost_tip: exit_data_lost_tip,
    if_exit_gantt: if_exit_gantt,
    pk_gantt_loading: pk_gantt_loading,
    gantt_load_fail: gantt_load_fail,
    operate_error: operate_error,
    gantt_keep_work_tip: gantt_keep_work_tip,
    save_success: save_success,
    gantt_frame_load_fail: gantt_frame_load_fail,
    pk_network_exception: pk_network_exception,
    save_fail: save_fail,
    pk_saving: pk_saving,
    gantt_plugin_fail: gantt_plugin_fail,
    gantt_editing_tip: gantt_editing_tip,
    pk_discard: pk_discard,
    pk_zoom_in: pk_zoom_in,
    pk_zoom_out: pk_zoom_out,
    pk_use: pk_use,
    draft_edit_tip: draft_edit_tip,
    if_use_local_draft: if_use_local_draft,
    pk_load_fail: pk_load_fail,
    pk_this_time: pk_this_time,
    choose_time: choose_time,
    pk_hh_mm_ss: pk_hh_mm_ss,
    pk_y_d: pk_y_d,
    pk_y_d_ts: pk_y_d_ts,
    pk_text: pk_text,
    h1: h1,
    h2: h2,
    h3: h3,
    h4: h4,
    h5: h5,
    h6: h6,
    pk_catalog: pk_catalog,
    page_view_load_fail_click: page_view_load_fail_click,
    page_tree_no_sub_doc: page_tree_no_sub_doc,
    page_tree_no_perm: page_tree_no_perm,
    pk_load_fail_retry: pk_load_fail_retry,
    loading2: loading2,
    page_tree_deleted: page_tree_deleted,
    pk_pause: pk_pause,
    pk_mute: pk_mute,
    pk_untitled: pk_untitled,
    pk_play: pk_play,
    adjust_volume: adjust_volume,
    download_fail_retry: download_fail_retry,
    pk_download: pk_download,
    get_file_fail: get_file_fail,
    pk_network_fail: pk_network_fail,
    pk_upload_fail: pk_upload_fail,
    audio_not_support_tip: audio_not_support_tip,
    pk_volume: pk_volume,
    browser_not_support_tip: browser_not_support_tip,
    pk_zoom_in2: pk_zoom_in2,
    video_upload_tip: video_upload_tip,
    pk_video: pk_video,
    pk_zoom_out2: pk_zoom_out2,
    space_update_fail_tip: space_update_fail_tip,
    pk_recent_update: pk_recent_update,
    show_more: show_more,
    no_space_update: no_space_update,
    pk_restore: pk_restore
};

var locale$1 = "zh";
var version$1 = 16;
var cannot_search_user$1 = "未搜索到用户，点击空格完成输入";
var load_fail_retry$1 = "加载失败，请稍后重试";
var pk_avatar$1 = "头像";
var mention_by_mis_name$1 = "可通过姓名及mis号@用户";
var searching$1 = "搜索中...";
var pk_link_text$1 = "链接文字";
var insert_blank$1 = "插 入";
var pk_link$1 = "链接";
var pk_link_address$1 = "链接地址";
var pk_visit_link$1 = "访问链接";
var cancel_blank$1 = "取 消";
var pk_link_change_tip$1 = "链接文字跟随文档标题变更";
var pk_edit_link$1 = "编辑链接";
var pk_cancel_link$1 = "取消链接";
var pk_link_text_tip$1 = "请输入链接文字";
var save_blank$1 = "保 存";
var pk_link_address_tip$1 = "请输入链接地址";
var border$1 = "边框";
var pk_insert$1 = "插入";
var image_link$1 = "图片链接";
var pk_edit$1 = "编辑";
var pk_retry$1 = "重试";
var width$1 = "宽度";
var reupload$1 = "重新上传";
var image_upload_fail1$1 = "抱歉，图片上传失败！";
var image_upload_fail3$1 = "否则，请点击下方按钮进行重试。";
var right_address_tip$1 = "请输入正确的地址";
var image_upload_fail2$1 = "如果是从外部拖动、粘贴过来的图片，请下载原图并重新上传。";
var pk_confirm$1 = "确定";
var input_html$1 = "输入HTML代码";
var formual$1 = "公式";
var pk_loading$1 = "正在加载中...";
var pk_cancel$1 = "取消";
var click_to_expand$1 = "点击展开内容";
var input_html_tip$1 = "可以插入HTML代码，以在页面内嵌入相应内容：";
var insert_latex_tip$1 = "可以插入Latex代码，以在页面内嵌入相应内容：";
var input_latex$1 = "输入Latex代码";
var pk_title$1 = "标题";
var wire_frame$1 = "线框";
var fill_in$1 = "填充";
var input_plantuml$1 = "輸入PlantUML代碼";
var border_style$1 = "边框样式";
var set_th_row$1 = "设置表头行";
var set_th_line$1 = "设置表头列";
var table_attr$1 = "表格属性";
var cancel_th_line$1 = "取消表头列";
var insert_row$1 = "删除行";
var insert_line_right$1 = "右侧插入列";
var split_cell$1 = "拆分单元格";
var delete_line$1 = "删除列";
var confirm_blank$1 = "确 定";
var input_markdown_tip$1 = "可以插入Markdown代码，以在页面内嵌入相应内容：";
var pk_no$1 = "无";
var cancel_th_row$1 = "取消表头行";
var insert_row_above$1 = "上方插入行";
var delete_table$1 = "删除表格";
var input_plantuml_tip$1 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容";
var border_width$1 = "边框宽度";
var input_markdown$1 = "输入Markdown代码";
var merge_cell$1 = "合并单元格";
var set_line_num$1 = "设置行号列";
var border_color$1 = "边框颜色";
var insert_line_left$1 = "左侧插入列";
var insert_row_below$1 = "下方插入行";
var cancel_line_num$1 = "取消行号列";
var if_exit_gantt$1 = "确定退出甘特图?";
var exit_data_lost_tip$1 = "退出后，尚未保存的数据将丢失。";
var gantt_load_fail$1 = "甘特图载入失败";
var pk_gantt_loading$1 = "甘特图载入中...";
var operate_error$1 = "您的操作有误";
var gantt_keep_work_tip$1 = "请至少保留一个任务";
var gantt_frame_load_fail$1 = "甘特图框架加载失败";
var save_success$1 = "保存成功";
var save_fail$1 = "保存失败";
var pk_network_exception$1 = "网络异常";
var pk_saving$1 = "正在保存中...";
var gantt_plugin_fail$1 = "甘特图编辑插件启动失败";
var gantt_editing_tip$1 = "正在编辑甘特图，请稍候再试噢。";
var pk_discard$1 = "废弃";
var pk_zoom_in$1 = "放大查看";
var pk_zoom_out$1 = "缩小查看";
var pk_use$1 = "使用";
var draft_edit_tip$1 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继续编辑";
var pk_load_fail$1 = "加载失败";
var if_use_local_draft$1 = "是否使用本地草稿？";
var pk_this_time$1 = "此刻";
var choose_time$1 = "选择时间";
var pk_hh_mm_ss$1 = "HH:mm:ss";
var pk_y_d$1 = "YYYY-MM-DD";
var pk_y_d_ts$1 = "YYYY-MM-DD HH:mm:ss";
var pk_text$1 = "正文";
var h1$1 = "标题 1";
var h2$1 = "标题 2";
var h3$1 = "标题 3";
var h4$1 = "标题 4";
var h5$1 = "标题 5";
var h6$1 = "标题 6";
var pk_catalog$1 = "目录";
var page_view_load_fail_click$1 = "目录树加载失败，请点击重新加载";
var page_tree_no_sub_doc$1 = "该目录下暂无子文档";
var page_tree_no_perm$1 = "无权查看该目录树";
var pk_load_fail_retry$1 = "加载失败，请点击重新加载";
var loading2$1 = "正在加载";
var page_tree_deleted$1 = "该目录树已被删除";
var pk_pause$1 = "暂停";
var pk_mute$1 = "静音";
var pk_untitled$1 = "未命名";
var pk_play$1 = "播放";
var adjust_volume$1 = "音量调节";
var download_fail_retry$1 = "下载失败，请重试";
var get_file_fail$1 = "文件获取失败";
var pk_download$1 = "下载";
var pk_network_fail$1 = "网络连接失败";
var pk_upload_fail$1 = "上传失败";
var audio_not_support_tip$1 = "本浏览器不支持 HTML5 audio 标签";
var browser_not_support_tip$1 = "本浏览器不支持 HTML5 video 标签";
var pk_volume$1 = "音量";
var pk_zoom_in2$1 = "放大";
var pk_video$1 = "视频";
var video_upload_tip$1 = "视频正在转码中，如文件较大可能时间较长，请耐心等待…";
var pk_zoom_out2$1 = "缩小";
var space_update_fail_tip$1 = "空间更新获取失败，点击重试";
var pk_recent_update$1 = "最近更新";
var show_more$1 = "显示更多";
var no_space_update$1 = "空间暂时没有动态～";
var pk_restore$1 = "恢复默认";
var zh_parker = {
    locale: locale$1,
    version: version$1,
    cannot_search_user: cannot_search_user$1,
    load_fail_retry: load_fail_retry$1,
    pk_avatar: pk_avatar$1,
    mention_by_mis_name: mention_by_mis_name$1,
    searching: searching$1,
    pk_link_text: pk_link_text$1,
    insert_blank: insert_blank$1,
    pk_link: pk_link$1,
    pk_link_address: pk_link_address$1,
    pk_visit_link: pk_visit_link$1,
    cancel_blank: cancel_blank$1,
    pk_link_change_tip: pk_link_change_tip$1,
    pk_edit_link: pk_edit_link$1,
    pk_cancel_link: pk_cancel_link$1,
    pk_link_text_tip: pk_link_text_tip$1,
    save_blank: save_blank$1,
    pk_link_address_tip: pk_link_address_tip$1,
    border: border$1,
    pk_insert: pk_insert$1,
    image_link: image_link$1,
    pk_edit: pk_edit$1,
    pk_retry: pk_retry$1,
    width: width$1,
    reupload: reupload$1,
    image_upload_fail1: image_upload_fail1$1,
    image_upload_fail3: image_upload_fail3$1,
    right_address_tip: right_address_tip$1,
    image_upload_fail2: image_upload_fail2$1,
    pk_confirm: pk_confirm$1,
    input_html: input_html$1,
    formual: formual$1,
    pk_loading: pk_loading$1,
    pk_cancel: pk_cancel$1,
    click_to_expand: click_to_expand$1,
    input_html_tip: input_html_tip$1,
    insert_latex_tip: insert_latex_tip$1,
    input_latex: input_latex$1,
    pk_title: pk_title$1,
    wire_frame: wire_frame$1,
    fill_in: fill_in$1,
    input_plantuml: input_plantuml$1,
    border_style: border_style$1,
    set_th_row: set_th_row$1,
    set_th_line: set_th_line$1,
    table_attr: table_attr$1,
    cancel_th_line: cancel_th_line$1,
    insert_row: insert_row$1,
    insert_line_right: insert_line_right$1,
    split_cell: split_cell$1,
    delete_line: delete_line$1,
    confirm_blank: confirm_blank$1,
    input_markdown_tip: input_markdown_tip$1,
    pk_no: pk_no$1,
    cancel_th_row: cancel_th_row$1,
    insert_row_above: insert_row_above$1,
    delete_table: delete_table$1,
    input_plantuml_tip: input_plantuml_tip$1,
    border_width: border_width$1,
    input_markdown: input_markdown$1,
    merge_cell: merge_cell$1,
    set_line_num: set_line_num$1,
    border_color: border_color$1,
    insert_line_left: insert_line_left$1,
    insert_row_below: insert_row_below$1,
    cancel_line_num: cancel_line_num$1,
    if_exit_gantt: if_exit_gantt$1,
    exit_data_lost_tip: exit_data_lost_tip$1,
    gantt_load_fail: gantt_load_fail$1,
    pk_gantt_loading: pk_gantt_loading$1,
    operate_error: operate_error$1,
    gantt_keep_work_tip: gantt_keep_work_tip$1,
    gantt_frame_load_fail: gantt_frame_load_fail$1,
    save_success: save_success$1,
    save_fail: save_fail$1,
    pk_network_exception: pk_network_exception$1,
    pk_saving: pk_saving$1,
    gantt_plugin_fail: gantt_plugin_fail$1,
    gantt_editing_tip: gantt_editing_tip$1,
    pk_discard: pk_discard$1,
    pk_zoom_in: pk_zoom_in$1,
    pk_zoom_out: pk_zoom_out$1,
    pk_use: pk_use$1,
    draft_edit_tip: draft_edit_tip$1,
    pk_load_fail: pk_load_fail$1,
    if_use_local_draft: if_use_local_draft$1,
    pk_this_time: pk_this_time$1,
    choose_time: choose_time$1,
    pk_hh_mm_ss: pk_hh_mm_ss$1,
    pk_y_d: pk_y_d$1,
    pk_y_d_ts: pk_y_d_ts$1,
    pk_text: pk_text$1,
    h1: h1$1,
    h2: h2$1,
    h3: h3$1,
    h4: h4$1,
    h5: h5$1,
    h6: h6$1,
    pk_catalog: pk_catalog$1,
    page_view_load_fail_click: page_view_load_fail_click$1,
    page_tree_no_sub_doc: page_tree_no_sub_doc$1,
    page_tree_no_perm: page_tree_no_perm$1,
    pk_load_fail_retry: pk_load_fail_retry$1,
    loading2: loading2$1,
    page_tree_deleted: page_tree_deleted$1,
    pk_pause: pk_pause$1,
    pk_mute: pk_mute$1,
    pk_untitled: pk_untitled$1,
    pk_play: pk_play$1,
    adjust_volume: adjust_volume$1,
    download_fail_retry: download_fail_retry$1,
    get_file_fail: get_file_fail$1,
    pk_download: pk_download$1,
    pk_network_fail: pk_network_fail$1,
    pk_upload_fail: pk_upload_fail$1,
    audio_not_support_tip: audio_not_support_tip$1,
    browser_not_support_tip: browser_not_support_tip$1,
    pk_volume: pk_volume$1,
    pk_zoom_in2: pk_zoom_in2$1,
    pk_video: pk_video$1,
    video_upload_tip: video_upload_tip$1,
    pk_zoom_out2: pk_zoom_out2$1,
    space_update_fail_tip: space_update_fail_tip$1,
    pk_recent_update: pk_recent_update$1,
    show_more: show_more$1,
    no_space_update: no_space_update$1,
    pk_restore: pk_restore$1
};

var locale$2 = "zh-HK";
var version$2 = 16;
var cannot_search_user$2 = "未能找到用户，請按空格鍵完成輸入";
var load_fail_retry$2 = "載入失敗，請稍後重試。";
var pk_avatar$2 = "頭像";
var mention_by_mis_name$2 = "可通過姓名及MIS號碼@用戶";
var searching$2 = "搜尋中...";
var pk_link_text$2 = "鏈接文字";
var insert_blank$2 = "插 入";
var pk_visit_link$2 = "访问鏈接";
var pk_link_address$2 = "鏈接地址";
var pk_link$2 = "鏈接";
var cancel_blank$2 = "取 消";
var pk_link_change_tip$2 = "鏈接文字跟隨文件標題變更";
var pk_edit_link$2 = "編輯鏈接";
var pk_link_text_tip$2 = "請輸入鏈接文字";
var pk_cancel_link$2 = "取消鏈接";
var save_blank$2 = "保 存";
var pk_link_address_tip$2 = "請輸入鏈接地址";
var border$2 = "邊框";
var pk_insert$2 = "插入";
var image_link$2 = "圖片鏈接";
var pk_retry$2 = "重試";
var pk_edit$2 = "編輯";
var width$2 = "寬度";
var reupload$2 = "重新上傳";
var image_upload_fail1$2 = "抱歉，圖片上傳失敗！";
var right_address_tip$2 = "請輸入正確的地址";
var image_upload_fail3$2 = "否則，請按下方按鈕進行重試。";
var image_upload_fail2$2 = "如果您從外部拖動、粘貼過來的圖片，請下載原圖並重新上傳。";
var pk_confirm$2 = "確定";
var input_html$2 = "輸入HTML代碼";
var formual$2 = "公式";
var pk_loading$2 = "正在載入中...";
var pk_cancel$2 = "取消";
var click_to_expand$2 = "點選展開內容";
var input_html_tip$2 = "可以插入HTML代碼，以在頁面內嵌入相應內容：";
var insert_latex_tip$2 = "可以插入Latex代碼，以在頁面內嵌入相應內容：";
var input_latex$2 = "输入LaTeX代码";
var pk_title$2 = "標題";
var wire_frame$2 = "線框";
var fill_in$2 = "填充";
var input_plantuml$2 = "輸入PlantUML程式碼";
var border_style$2 = "邊框樣式";
var set_th_row$2 = "設置表頭行";
var set_th_line$2 = "設置表頭列";
var table_attr$2 = "表格屬性";
var cancel_th_line$2 = "取消表頭列";
var insert_row$2 = "删除行";
var insert_line_right$2 = "右側插入列";
var split_cell$2 = "拆分儲存格";
var delete_line$2 = "删除列";
var pk_no$2 = "無";
var confirm_blank$2 = "確 定";
var input_markdown_tip$2 = "可以插入Markdown代碼，以在頁面內嵌入相應內容：";
var cancel_th_row$2 = "取消表頭行";
var insert_row_above$2 = "上方插入行";
var delete_table$2 = "刪除表格";
var input_plantuml_tip$2 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容：";
var border_width$2 = "邊框寬度";
var input_markdown$2 = "輸入Markdown代碼";
var merge_cell$2 = "合併儲存格";
var set_line_num$2 = "設置行号列";
var border_color$2 = "邊框顏色";
var insert_line_left$2 = "左側插入列";
var insert_row_below$2 = "下方插入行";
var cancel_line_num$2 = "取消行号列";
var if_exit_gantt$2 = "確定退出甘特圖?";
var exit_data_lost_tip$2 = "退出後，尚未儲存的數據將會遺失。";
var pk_gantt_loading$2 = "甘特圖載入中...";
var gantt_load_fail$2 = "甘特圖載入失敗";
var operate_error$2 = "您的操作有誤";
var gantt_keep_work_tip$2 = "請至少保留一個任務";
var save_success$2 = "保存成功";
var gantt_frame_load_fail$2 = "甘特圖框架載入失敗";
var save_fail$2 = "保存失敗";
var pk_network_exception$2 = "網絡異常";
var pk_saving$2 = "正在儲存中...";
var gantt_plugin_fail$2 = "甘特圖編輯插件啟動失敗。";
var gantt_editing_tip$2 = "正在編輯甘特圖，請稍後再試唷。";
var pk_discard$2 = "廢棄";
var pk_zoom_in$2 = "放大查看";
var pk_zoom_out$2 = "縮小查看";
var pk_use$2 = "使用";
var draft_edit_tip$2 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继續編輯";
var pk_load_fail$2 = "加載失敗";
var if_use_local_draft$2 = "是否使用本地草稿？";
var pk_this_time$2 = "此刻";
var choose_time$2 = "選擇時間";
var pk_hh_mm_ss$2 = "HH:mm:ss";
var pk_y_d$2 = "YYYY-MM-DD";
var pk_y_d_ts$2 = "YYYY-MM-DD HH:mm:ss";
var pk_text$2 = "正文";
var h1$2 = "標題 1";
var h2$2 = "標題 2";
var h3$2 = "標題 3";
var h4$2 = "標題 4";
var h5$2 = "標題 5";
var h6$2 = "標題 6";
var pk_catalog$2 = "目錄";
var page_view_load_fail_click$2 = "目錄樹加載失敗，請點擊重新加載。";
var page_tree_no_sub_doc$2 = "該目錄下暫無子文檔。";
var page_tree_no_perm$2 = "無權查看該目錄樹";
var pk_load_fail_retry$2 = "加載失敗，請點擊重新加載。";
var loading2$2 = "正在載入";
var page_tree_deleted$2 = "該目錄樹已被刪除";
var pk_pause$2 = "暫停";
var pk_mute$2 = "靜音";
var pk_untitled$2 = "未命名";
var pk_play$2 = "播放";
var adjust_volume$2 = "音量調節";
var download_fail_retry$2 = "下載失敗，請重試";
var pk_download$2 = "下載";
var get_file_fail$2 = "文件獲取失敗";
var pk_network_fail$2 = "網絡連接失敗";
var pk_upload_fail$2 = "上載失敗";
var audio_not_support_tip$2 = "本瀏覽器不支援 HTML5 audio 標籤";
var pk_volume$2 = "音量";
var browser_not_support_tip$2 = "本瀏覽器不支援 HTML5 video 標籤";
var pk_zoom_in2$2 = "放大";
var video_upload_tip$2 = "視頻正在轉碼中，如果文件較大可能需要較長的時間，請耐心等待...";
var pk_video$2 = "視頻";
var pk_zoom_out2$2 = "縮小";
var space_update_fail_tip$2 = "空間更新獲取失敗，點擊重試";
var pk_recent_update$2 = "最近更新";
var show_more$2 = "顯示更多";
var no_space_update$2 = "空間暫時沒有動態～";
var pk_restore$2 = "恢復預設";
var zhHK_parker = {
    locale: locale$2,
    version: version$2,
    cannot_search_user: cannot_search_user$2,
    load_fail_retry: load_fail_retry$2,
    pk_avatar: pk_avatar$2,
    mention_by_mis_name: mention_by_mis_name$2,
    searching: searching$2,
    pk_link_text: pk_link_text$2,
    insert_blank: insert_blank$2,
    pk_visit_link: pk_visit_link$2,
    pk_link_address: pk_link_address$2,
    pk_link: pk_link$2,
    cancel_blank: cancel_blank$2,
    pk_link_change_tip: pk_link_change_tip$2,
    pk_edit_link: pk_edit_link$2,
    pk_link_text_tip: pk_link_text_tip$2,
    pk_cancel_link: pk_cancel_link$2,
    save_blank: save_blank$2,
    pk_link_address_tip: pk_link_address_tip$2,
    border: border$2,
    pk_insert: pk_insert$2,
    image_link: image_link$2,
    pk_retry: pk_retry$2,
    pk_edit: pk_edit$2,
    width: width$2,
    reupload: reupload$2,
    image_upload_fail1: image_upload_fail1$2,
    right_address_tip: right_address_tip$2,
    image_upload_fail3: image_upload_fail3$2,
    image_upload_fail2: image_upload_fail2$2,
    pk_confirm: pk_confirm$2,
    input_html: input_html$2,
    formual: formual$2,
    pk_loading: pk_loading$2,
    pk_cancel: pk_cancel$2,
    click_to_expand: click_to_expand$2,
    input_html_tip: input_html_tip$2,
    insert_latex_tip: insert_latex_tip$2,
    input_latex: input_latex$2,
    pk_title: pk_title$2,
    wire_frame: wire_frame$2,
    fill_in: fill_in$2,
    input_plantuml: input_plantuml$2,
    border_style: border_style$2,
    set_th_row: set_th_row$2,
    set_th_line: set_th_line$2,
    table_attr: table_attr$2,
    cancel_th_line: cancel_th_line$2,
    insert_row: insert_row$2,
    insert_line_right: insert_line_right$2,
    split_cell: split_cell$2,
    delete_line: delete_line$2,
    pk_no: pk_no$2,
    confirm_blank: confirm_blank$2,
    input_markdown_tip: input_markdown_tip$2,
    cancel_th_row: cancel_th_row$2,
    insert_row_above: insert_row_above$2,
    delete_table: delete_table$2,
    input_plantuml_tip: input_plantuml_tip$2,
    border_width: border_width$2,
    input_markdown: input_markdown$2,
    merge_cell: merge_cell$2,
    set_line_num: set_line_num$2,
    border_color: border_color$2,
    insert_line_left: insert_line_left$2,
    insert_row_below: insert_row_below$2,
    cancel_line_num: cancel_line_num$2,
    if_exit_gantt: if_exit_gantt$2,
    exit_data_lost_tip: exit_data_lost_tip$2,
    pk_gantt_loading: pk_gantt_loading$2,
    gantt_load_fail: gantt_load_fail$2,
    operate_error: operate_error$2,
    gantt_keep_work_tip: gantt_keep_work_tip$2,
    save_success: save_success$2,
    gantt_frame_load_fail: gantt_frame_load_fail$2,
    save_fail: save_fail$2,
    pk_network_exception: pk_network_exception$2,
    pk_saving: pk_saving$2,
    gantt_plugin_fail: gantt_plugin_fail$2,
    gantt_editing_tip: gantt_editing_tip$2,
    pk_discard: pk_discard$2,
    pk_zoom_in: pk_zoom_in$2,
    pk_zoom_out: pk_zoom_out$2,
    pk_use: pk_use$2,
    draft_edit_tip: draft_edit_tip$2,
    pk_load_fail: pk_load_fail$2,
    if_use_local_draft: if_use_local_draft$2,
    pk_this_time: pk_this_time$2,
    choose_time: choose_time$2,
    pk_hh_mm_ss: pk_hh_mm_ss$2,
    pk_y_d: pk_y_d$2,
    pk_y_d_ts: pk_y_d_ts$2,
    pk_text: pk_text$2,
    h1: h1$2,
    h2: h2$2,
    h3: h3$2,
    h4: h4$2,
    h5: h5$2,
    h6: h6$2,
    pk_catalog: pk_catalog$2,
    page_view_load_fail_click: page_view_load_fail_click$2,
    page_tree_no_sub_doc: page_tree_no_sub_doc$2,
    page_tree_no_perm: page_tree_no_perm$2,
    pk_load_fail_retry: pk_load_fail_retry$2,
    loading2: loading2$2,
    page_tree_deleted: page_tree_deleted$2,
    pk_pause: pk_pause$2,
    pk_mute: pk_mute$2,
    pk_untitled: pk_untitled$2,
    pk_play: pk_play$2,
    adjust_volume: adjust_volume$2,
    download_fail_retry: download_fail_retry$2,
    pk_download: pk_download$2,
    get_file_fail: get_file_fail$2,
    pk_network_fail: pk_network_fail$2,
    pk_upload_fail: pk_upload_fail$2,
    audio_not_support_tip: audio_not_support_tip$2,
    pk_volume: pk_volume$2,
    browser_not_support_tip: browser_not_support_tip$2,
    pk_zoom_in2: pk_zoom_in2$2,
    video_upload_tip: video_upload_tip$2,
    pk_video: pk_video$2,
    pk_zoom_out2: pk_zoom_out2$2,
    space_update_fail_tip: space_update_fail_tip$2,
    pk_recent_update: pk_recent_update$2,
    show_more: show_more$2,
    no_space_update: no_space_update$2,
    pk_restore: pk_restore$2
};

// This file was auto generated by resource-sync tools.
var resources = {
    "en": {
        parker: en_parker
    },
    "zh": {
        parker: zh_parker
    },
    "zh-HK": {
        parker: zhHK_parker
    }
};

function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var I18nManager = /*#__PURE__*/ function() {
    function I18nManager() {
        _classCallCheck(this, I18nManager);
    }
    I18nManager.getInstance = function getInstance() {
        if (this.instance === null) {
            this.instance = createInstance();
            this.instance.init({
                // lng: 'zh',
                fallbackLng: "zh",
                ns: [
                    "parker"
                ],
                defaultNS: "parker",
                resources: resources,
                interpolation: {
                    escapeValue: false
                },
                react: {
                    useSuspense: false
                }
            });
            window.parkerI18nClient = this.instance;
        }
        return this.instance;
    };
    return I18nManager;
}();
I18nManager.instance = null;
var i18nClient = I18nManager.getInstance();

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperty$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _objectSpread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var VIDEO_DEFAULT_WIDTH = 512;
var VIDEO_DEFAULT_HEIGHT = 288;
var PROGRESS_DEFAULT_WIDTH = 235;
// const { isMobile } = getSystemInfo(); // isMobile主要用于toView场景的处理
var getPositiveNumber = function(num) {
    return num < 0 ? 0 : num;
};
var Video = /*#__PURE__*/ function(Component) {
    _inherits(Video, Component);
    var _super = _createSuper(Video);
    function Video(props) {
        _classCallCheck$1(this, Video);
        var _this;
        _this = _super.call(this, props);
        _this.isOnProgressMouseDown = false // 鼠标是否在视频进度条按下状态
        ;
        _this.isOnVolumeMouseDown = false // 鼠标是否在volumeBar（音量条）按下状态
        ;
        _this.state = {
            init: true,
            volume: 0.5,
            isMute: false,
            progress: 0,
            playing: 0,
            current: "00:00",
            total: "00:00",
            isPlay: false,
            hasTip: false,
            tipInfo: {
                // 鼠标在进度条上的时间提示
                time: "00:00",
                offsetX: 0
            },
            showToolBar: true,
            showVolumeBar: false,
            isFullScreen: false,
            isError: false,
            progressWidth: PROGRESS_DEFAULT_WIDTH,
            downloadStatus: 0,
            isResizing: false
        };
        _this.observer = null // 增加observer订阅侧边栏变化的事件，更新进度条实际宽度。目前没有想到更好的办法解决这个问题
        ;
        /**
   * 全屏
   */ _this.handleFullScreenChange = function() {
            var isFullScreen = document.isFullScreen || document.mozIsFullScreen || document.webkitIsFullScreen || document.msIsFullScreen;
            if (!isFullScreen) _this.setState({
                isFullScreen: false
            });
        };
        /**
   * 切换播放暂停
   */ _this.togglePlay = function() {
            var isPlay = _this.state.isPlay;
            _this.setState({
                isPlay: !isPlay
            });
            if (isPlay) {
                var _this_video;
                (_this_video = _this.video) === null || _this_video === void 0 ? void 0 : _this_video.pause();
            } else {
                document.querySelectorAll("audio").forEach(function(otherAudio) {
                    return otherAudio.pause();
                });
                document.querySelectorAll("video").forEach(function(otherVideo) {
                    return otherVideo !== _this.video && otherVideo.pause();
                });
                // this.video && this.video.play()
                if (_this.video) {
                    var playPromise = _this.video.play();
                    if (playPromise !== undefined) {
                        playPromise.then(function() {}).catch(function(e) {
                            console.error(e);
                        });
                    }
                }
            }
        };
        /**
   * 浏览器可以开始播放时触发
   */ _this.handleCanPlay = function() {
            var duration = getPositiveNumber(_this.video.duration);
            var total = formatTime(duration);
            _this.setState({
                total: total
            });
        };
        /**
   * 视频正在下载触发
   */ _this.handleProgress = function() {
            if (_this.video.buffered.length <= 0) {
                return;
            }
            var progress = getPositiveNumber(_this.video.buffered.end(0) / _this.video.duration);
            _this.setState({
                progress: progress
            });
        };
        /**
   * 视频暂停时触发
   */ _this.handlePause = function() {
            if (document.hidden) return;
            var _this_state = _this.state, isPlay = _this_state.isPlay, showToolBar = _this_state.showToolBar;
            if (isPlay) {
                _this.setState({
                    isPlay: false
                });
            }
            if (!showToolBar) _this.setState({
                showToolBar: true
            });
        };
        /**
   * 播放位置改变重新定位
   */ _this.handleTimeUpdate = function() {
            var currentTime = getPositiveNumber(_this.video.currentTime);
            var current = formatTime(currentTime);
            var playing = currentTime / _this.video.duration;
            _this.setState({
                current: current,
                playing: playing
            });
            var init = _this.state.init;
            if (init) _this.setState({
                init: false
            });
        };
        _this.handleVideoMouseUp = function() {
            _this.isOnProgressMouseDown = false;
        };
        /**
   * 视频区域鼠标移动响应，进度条拖动则更新进度
   */ _this.handleVideoMouseMove = function(e) {
            var _this_state = _this.state, isFullScreen = _this_state.isFullScreen, progressWidth = _this_state.progressWidth, showToolBar = _this_state.showToolBar;
            var left = _this.progressBar.getBoundingClientRect().left;
            var zoom = _this.props.zoom;
            var clientX = e.nativeEvent.clientX / zoom;
            var dis = clientX - left;
            var calculateProgressWidth = isFullScreen ? PROGRESS_DEFAULT_WIDTH : progressWidth;
            var newCurrentTime = getPositiveNumber(dis / calculateProgressWidth * _this.video.duration);
            if (_this.isOnProgressMouseDown) {
                _this.video.currentTime = newCurrentTime;
                _this.handleTimeUpdate();
            }
            if (!showToolBar) _this.setState({
                showToolBar: true
            });
            _this.toggleToolbarDebounce();
        };
        _this.handleVideoMouseLeave = function() {
            _this.isOnProgressMouseDown = false;
        };
        /**
   * 鼠标按下进度条某处
   */ _this.handleProgressBarMouseDown = function(e) {
            var _this_state = _this.state, isFullScreen = _this_state.isFullScreen, progressWidth = _this_state.progressWidth;
            var calculateProgressWidth = isFullScreen ? PROGRESS_DEFAULT_WIDTH : progressWidth;
            var zoom = _this.props.zoom;
            var left = _this.progressBar.getBoundingClientRect().left;
            var clientX = e.nativeEvent.clientX / zoom;
            var dis = clientX - left;
            var newCurrentTime = getPositiveNumber(dis / calculateProgressWidth * _this.video.duration);
            _this.video.currentTime = newCurrentTime;
            _this.isOnProgressMouseDown = true;
            e.preventDefault();
        };
        /**
   * 进度条上的鼠标释放
   */ _this.handleProgressBarMouseUp = function() {
            _this.isOnProgressMouseDown = false;
        };
        /**
   * 进度条区域鼠标移动响应，设置鼠标位置时间提示
   */ _this.handleProgressMouseMove = function(e) {
            var _this_state = _this.state, isFullScreen = _this_state.isFullScreen, progressWidth = _this_state.progressWidth;
            var calculateProgressWidth = isFullScreen ? PROGRESS_DEFAULT_WIDTH : progressWidth;
            var zoom = _this.props.zoom;
            var left = _this.progressBar.getBoundingClientRect().left;
            var clientX = e.nativeEvent.clientX / zoom;
            var dis = clientX - left;
            var newCurrentTime = dis / calculateProgressWidth * _this.video.duration;
            if (newCurrentTime > _this.video.duration || newCurrentTime < 0) {
                _this.setState({
                    hasTip: false
                });
                return;
            }
            _this.setState({
                hasTip: true,
                tipInfo: {
                    time: formatTime(newCurrentTime),
                    offsetX: dis
                }
            });
        };
        /**
   * 在音量条按下鼠标后移动调整音量
   */ _this.handleVolumeBarMouseDown = function(e) {
            var bottom = _this.volumeBar.getBoundingClientRect().bottom;
            var zoom = _this.props.zoom;
            var clientY = e.nativeEvent.clientY / zoom;
            var dis = bottom - clientY;
            var volume = (dis > 0 ? dis : 0) / 80;
            volume = volume > 1 ? 1 : volume;
            _this.video.volume = volume;
            _this.setState({
                volume: volume,
                isMute: false
            });
            _this.isOnVolumeMouseDown = true;
            e.preventDefault();
        };
        /**
   * 音量条上的鼠标释放
   */ _this.handleVolumeBarMouseUp = function() {
            _this.isOnVolumeMouseDown = false;
        };
        /**
   * 在音量条按下鼠标后移动调整音量
   */ _this.handleVolumeBarMouseMove = function(e) {
            if (_this.isOnVolumeMouseDown) {
                _this.handleVolumeBarMouseDown(e);
            }
        };
        _this.showVolumeBar = function() {
            _this.setState({
                showVolumeBar: true
            });
        };
        _this.hideVolumeBar = function() {
            _this.setState({
                showVolumeBar: false
            });
        };
        /**
   * 静音切换
   */ _this.toggleMute = function() {
            var _this_state = _this.state, volume = _this_state.volume, isMute = _this_state.isMute;
            if (isMute) {
                _this.video.volume = volume;
                _this.setState({
                    isMute: false
                });
            } else {
                _this.video.volume = 0;
                _this.setState({
                    isMute: true
                });
            }
        };
        /**
   * 双击全屏切换
   */ _this.toggleScale = function() {
            var isFullScreen = document.isFullScreen || document.mozIsFullScreen || document.webkitIsFullScreen || document.msIsFullScreen;
            if (isFullScreen) {
                var efs = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
                if (efs) {
                    efs.call(document);
                }
            } else {
                var rfs = _this.video.requestFullscreen || _this.video.webkitRequestFullscreen || _this.video.mozRequestFullScreen || _this.video.msRequestFullscreen;
                if (rfs) {
                    rfs.call(_this.video.parentElement.parentElement);
                }
                _this.setState({
                    isFullScreen: true
                });
            }
        };
        /**
   * 切换toolbar(视频下方工具栏包括进度条音量等)展示隐藏
   */ _this.toggleToolbar = function() {
            var _this_state = _this.state, isPlay = _this_state.isPlay, showToolBar = _this_state.showToolBar;
            if (isPlay && showToolBar) {
                _this.setState({
                    showToolBar: false
                });
            }
        };
        /**
   * 处理视频文件下载
   */ _this.handleDownload = function() {
            var _provider_handleUrl;
            var downloadStatus = _this.state.downloadStatus;
            if (downloadStatus === 1) return;
            var _this_props = _this.props, url = _this_props.url, name = _this_props.name, provider = _this_props.provider;
            var src = (provider === null || provider === void 0 ? void 0 : (_provider_handleUrl = provider.handleUrl) === null || _provider_handleUrl === void 0 ? void 0 : _provider_handleUrl.call(provider, url)) || url;
            provider.download(src, name);
        };
        _this.handleDragStart = function(e) {
            e.preventDefault();
        };
        /**
   * 视频文件异常错误处理，可以做一些异常上报的工作
   */ _this.handleError = function() {
            _this.setState({
                isError: true
            });
            var _this_props = _this.props, handleVideoError = _this_props.provider.handleVideoError, url = _this_props.url;
            // 提供给业务方进行视频错误处理
            handleVideoError === null || handleVideoError === void 0 ? void 0 : handleVideoError(_this.video.error, url);
        };
        /**
   * 文件获取失败重试
   */ _this.handleRetry = function() {
            _this.setState({
                isError: false
            });
        };
        _this.handleResizeStart = function() {
            _this.setState({
                isResizing: true
            });
        };
        /**
   * 调整完成后调用
   * @param size object 包括调整后的width、height
   */ _this.handleResizeEnd = function(size) {
            var _this_video;
            var _this_props = _this.props, view = _this_props.view, getPos = _this_props.getPos;
            var state = view.state, dispatch = view.dispatch;
            var tr = state.tr, doc = state.doc;
            var pos = getPos();
            var node = doc.nodeAt(pos);
            dispatch(tr.setNodeMarkup(pos, null, _objectSpreadProps(_objectSpread$2({}, node.attrs), {
                width: size.width,
                height: size.height
            })).setSelection(NodeSelection.create(tr.doc, pos)) // 重新设置选区
            );
            // 调整视频宏大小后需要重置进度
            _this.setState({
                current: "00:00",
                isPlay: false,
                playing: 0,
                progress: 0,
                progressWidth: _this.progressBar.clientWidth,
                isResizing: false
            });
            _this.video.currentTime = 0;
            (_this_video = _this.video) === null || _this_video === void 0 ? void 0 : _this_video.pause();
        };
        _this.toggleToolbarDebounce = debounce(_this.toggleToolbar, 3e3);
        return _this;
    }
    var _proto = Video.prototype;
    _proto.componentDidMount = function componentDidMount() {
        var _this = this;
        if (document.exitFullscreen) {
            document.addEventListener("fullscreenchange", this.handleFullScreenChange);
        } else if (document.webkitExitFullscreen) {
            document.addEventListener("webkitfullscreenchange", this.handleFullScreenChange);
        } else if (document.mozCancelFullScreen) {
            document.addEventListener("mozfullscreenchange", this.handleFullScreenChange);
        } else if (document.exitFullscreen) {
            document.addEventListener("MSFullscreenChange", this.handleFullScreenChange);
        }
        // if (!isMobile) {
        this.timer = setTimeout(function() {
            var _this_progressBar;
            if (((_this_progressBar = _this.progressBar) === null || _this_progressBar === void 0 ? void 0 : _this_progressBar.clientWidth) !== PROGRESS_DEFAULT_WIDTH) {
                _this.setState({
                    progressWidth: _this.progressBar.clientWidth
                });
            }
            // observer相关代码
            var target = document.querySelector(".resize-box");
            if (target) {
                var config = {
                    attributes: true
                };
                var resetWidth = function() {
                    if (_this.progressBar) {
                        _this.setState({
                            progressWidth: _this.progressBar.clientWidth
                        });
                    }
                };
                _this.observer = new MutationObserver(resetWidth);
                _this.observer.observe(target, config);
            }
        }, 0);
    // }
    };
    _proto.componentWillUnmount = function componentWillUnmount() {
        if (document.exitFullscreen) {
            document.removeEventListener("fullscreenchange", this.handleFullScreenChange);
        } else if (document.webkitExitFullscreen) {
            document.removeEventListener("webkitfullscreenchange", this.handleFullScreenChange);
        } else if (document.mozCancelFullScreen) {
            document.removeEventListener("mozfullscreenchange", this.handleFullScreenChange);
        } else if (document.msExitFullscreen) {
            document.removeEventListener("MSFullscreenChange", this.handleFullScreenChange);
        }
        this.timer && clearTimeout(this.timer);
        // !isMobile && this.observer && this.observer.disconnect();
        this.observer && this.observer.disconnect();
    };
    _proto.render = function render() {
        var _this = this;
        var _provider_handleUrl;
        var _this_props = this.props, url = _this_props.url, width = _this_props.width, height = _this_props.height, view = _this_props.view, getPos = _this_props.getPos, provider = _this_props.provider, resizer = _this_props.resizer;
        var _this_state = this.state, init = _this_state.init, volume = _this_state.volume, isMute = _this_state.isMute, progress = _this_state.progress, playing = _this_state.playing, current = _this_state.current, total = _this_state.total, isPlay = _this_state.isPlay, showToolBar = _this_state.showToolBar, showVolumeBar = _this_state.showVolumeBar, isFullScreen = _this_state.isFullScreen, isError = _this_state.isError, hasTip = _this_state.hasTip, _this_state_tipInfo = _this_state.tipInfo, time = _this_state_tipInfo.time, offsetX = _this_state_tipInfo.offsetX, downloadStatus = _this_state.downloadStatus, progressWidth = _this_state.progressWidth, isResizing = _this_state.isResizing;
        var calculateProgressWidth = isFullScreen ? PROGRESS_DEFAULT_WIDTH : progressWidth;
        // 业务方可以对url进行加工处理
        var src = (provider === null || provider === void 0 ? void 0 : (_provider_handleUrl = provider.handleUrl) === null || _provider_handleUrl === void 0 ? void 0 : _provider_handleUrl.call(provider, url)) || url;
        var VIDEO_WIDTH = +width < VIDEO_DEFAULT_WIDTH ? VIDEO_DEFAULT_WIDTH : +width;
        var VIDEO_HEIGHT = +height < VIDEO_DEFAULT_HEIGHT ? VIDEO_DEFAULT_HEIGHT : +height;
        var style = {
            width: "".concat(VIDEO_WIDTH, "px"),
            height: "".concat(VIDEO_HEIGHT, "px")
        };
        // const isPrint = window.location.href.includes("print"); // 是否为打印页面
        // if (isError && !isPrint) {
        if (isError) {
            return /*#__PURE__*/ React.createElement("div", {
                className: "".concat(classNamePrefix("video-view"), " error"),
                style: style
            }, /*#__PURE__*/ React.createElement("div", {
                className: "notice",
                "data-src": src
            }, window.navigator.onLine ? i18nClient.t("get_file_fail", "文件获取失败") : i18nClient.t("pk_network_fail", "网络连接失败")), /*#__PURE__*/ React.createElement("button", {
                className: "retry ".concat(classNamePrefix("btn")),
                onClick: this.handleRetry
            }, i18nClient.t("pk_retry", "重试")));
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: classNamePrefix("video-view"),
            style: style,
            onMouseUp: this.handleVideoMouseUp,
            onMouseMove: this.handleVideoMouseMove,
            onMouseLeave: this.handleVideoMouseLeave,
            onDragStart: this.handleDragStart,
            ref: function(ref) {
                _this.videoWrapper = ref;
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: classNames("middle", {
                pause: !isPlay
            }),
            onClick: this.togglePlay
        }, /*#__PURE__*/ React.createElement("video", {
            ref: function(ref) {
                _this.video = ref;
            },
            src: src,
            preload: "meta",
            volume: isMute ? 0 : volume,
            onCanPlay: this.handleCanPlay,
            onProgress: this.handleProgress,
            onTimeUpdate: this.handleTimeUpdate,
            onPause: this.handlePause,
            onDoubleClick: this.toggleScale,
            onError: this.handleError
        }, i18nClient.t("browser_not_support_tip", "本浏览器不支持 HTML5 video 标签"))), /*#__PURE__*/ React.createElement("div", {
            className: classNames("bottom", {
                hidden: !showToolBar
            })
        }, /*#__PURE__*/ React.createElement("div", {
            className: "play-btn",
            onClick: this.togglePlay
        }, !isPlay ? /*#__PURE__*/ React.createElement("img", {
            src: img,
            alt: i18nClient.t("pk_play", "播放")
        }) : /*#__PURE__*/ React.createElement("img", {
            src: img$1,
            alt: i18nClient.t("pk_pause", "暂停")
        })), /*#__PURE__*/ React.createElement("div", {
            className: "progress-bar",
            ref: function(ref) {
                _this.progressBar = ref;
            },
            onMouseDown: this.handleProgressBarMouseDown,
            onMouseMove: this.handleProgressMouseMove,
            onMouseLeave: function() {
                return _this.setState({
                    hasTip: false
                });
            },
            style: isResizing ? {
                overflow: "hidden"
            } : {}
        }, /*#__PURE__*/ React.createElement("div", {
            className: "load-bar",
            style: {
                width: "".concat(init ? 0 : progress * calculateProgressWidth, "px")
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames("played-bar", {
                dragged: this.isOnProgressMouseDown
            }),
            style: {
                width: "".concat(playing * calculateProgressWidth, "px")
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classNames("played-button", {
                dragged: this.isOnProgressMouseDown
            }),
            style: {
                left: "".concat(playing * calculateProgressWidth, "px")
            }
        }), hasTip && /*#__PURE__*/ React.createElement("div", {
            className: "progress-tip-wrapper",
            style: {
                left: "".concat(offsetX - 22, "px")
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: "progress-tip"
        }, time))), /*#__PURE__*/ React.createElement("div", {
            className: "time"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "current"
        }, current), /*#__PURE__*/ React.createElement("span", null, "/"), /*#__PURE__*/ React.createElement("span", {
            className: "total"
        }, total)), /*#__PURE__*/ React.createElement("div", {
            className: "volume-btn",
            onMouseEnter: this.showVolumeBar,
            onMouseLeave: this.hideVolumeBar,
            onDragStart: this.handleDragStart
        }, isMute || volume < 0.01 ? /*#__PURE__*/ React.createElement("img", {
            src: img$3,
            alt: i18nClient.t("pk_mute", "静音"),
            onClick: this.toggleMute
        }) : /*#__PURE__*/ React.createElement("img", {
            src: img$2,
            alt: i18nClient.t("pk_volume", "音量"),
            onClick: this.toggleMute
        }), showVolumeBar && /*#__PURE__*/ React.createElement("div", {
            className: "volume-bar-wrapper",
            onMouseMove: this.handleVolumeBarMouseMove,
            onMouseUp: this.handleVolumeBarMouseUp
        }, /*#__PURE__*/ React.createElement("div", {
            className: "volume-bar",
            ref: function(ref) {
                _this.volumeBar = ref;
            },
            onMouseDown: this.handleVolumeBarMouseDown
        }, /*#__PURE__*/ React.createElement("div", {
            className: "selected-bar",
            style: {
                height: "".concat(isMute ? 0 : volume * 80, "px")
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: "bar-btn"
        }))))), /*#__PURE__*/ React.createElement("div", {
            className: "scale-btn",
            onClick: this.toggleScale
        }, isFullScreen ? /*#__PURE__*/ React.createElement("img", {
            src: img$5,
            alt: i18nClient.t("pk_zoom_in2", "放大")
        }) : /*#__PURE__*/ React.createElement("img", {
            src: img$4,
            alt: i18nClient.t("pk_zoom_out2", "缩小")
        })), /*#__PURE__*/ React.createElement("div", {
            className: classNames("download-btn", {
                error: downloadStatus === 2,
                "video-loading": downloadStatus === 1
            }),
            onClick: this.handleDownload
        }, /*#__PURE__*/ React.createElement("div", {
            className: "tip-wrapper"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "tip"
        }, i18nClient.t("download_fail_retry", "下载失败，请重试"))), /*#__PURE__*/ React.createElement("img", {
            src: img$6,
            alt: i18nClient.t("pk_download", "下载")
        }))), this.videoWrapper && resizer && /*#__PURE__*/ React.createElement(Resizer, {
            $container: this.videoWrapper,
            view: view,
            getPos: getPos,
            initialWidth: VIDEO_WIDTH,
            initialHeight: VIDEO_HEIGHT,
            onResizeEnd: this.handleResizeEnd,
            onResizeStart: this.handleResizeStart,
            limit: {
                minWidth: VIDEO_DEFAULT_WIDTH,
                minHeight: VIDEO_DEFAULT_HEIGHT
            },
            fixedRatio: false,
            hideInfo: true
        }));
    };
    return Video;
}(Component);
Video.defaultProps = {
    zoom: 1,
    resizer: true
};

var css_248z$1 = '.ct-video{margin:8px 0;max-width:100%;width:-webkit-fit-content;width:-moz-fit-content;width:fit-content}.ct-video.align-center{margin:0 auto}.ct-video.align-right{float:right;margin-top:0}.ct-video.align-right+*,.ct-video.align-right+div+span+*{clear:both}.ct-video-view{background:#f6f6f6;border:1px solid #ddd;height:288px;max-width:100%;min-height:288px;min-width:512px;position:relative;width:512px}.ct-video-view img{vertical-align:unset}.ct-video-view:hover{border:1px solid #3d91f2;cursor:pointer}.ct-video-view .middle{background:#000;height:100%;width:100%}.ct-video-view .middle:after{content:"";opacity:0;position:absolute;transition:opacity .3s ease}.ct-video-view .middle video{display:block;height:100%;width:100%}.ct-video-view .middle.pause{position:relative}.ct-video-view .middle.pause:after{background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2218px%22 height%3D%2220px%22 viewBox%3D%220 0 18 20%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E9%A2%84%E8%A7%88%E6%92%AD%E6%94%BE%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22Artboard-2%22 transform%3D%22translate(-446.000000%2C -115.000000)%22 fill%3D%22%236B6B6B%22%3E            %3Cg id%3D%22Group-12%22 transform%3D%22translate(446.000000%2C 115.000000)%22%3E                %3Cpath d%3D%22M17.5258914%2C10.7953904 L1.4484631%2C19.8724989 C0.990863649%2C20.1308537 0.401755017%2C19.9841502 0.132652138%2C19.5448276 C0.0457979589%2C19.4030342 0%2C19.2415294 0%2C19.0770349 L3.41490118e-15%2C0.922817934 C3.41490118e-15%2C0.413159662 0.430348031%2C0 0.961209231%2C0 C1.1325471%2C0 1.30077079%2C0.0439687597 1.4484631%2C0.127353928 L17.5258914%2C9.20446242 C17.9834908%2C9.46281716 18.1362975%2C10.0283964 17.8671946%2C10.4677191 C17.7843081%2C10.603035 17.6668367%2C10.7158145 17.5258914%2C10.7953904 Z%22 id%3D%22Triangle-2%22%3E%3C%2Fpath%3E            %3C%2Fg%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 23px;background-color:#fff;border-radius:50%;height:60px;left:50%;margin-left:-30px;margin-top:-30px;opacity:1;top:50%;transition:opacity .3s ease;width:60px}.ct-video-view .middle.pause:before{background:rgba(0,0,0,.5);bottom:0;content:"";left:0;position:absolute;right:0;top:0}.ct-video-view .middle.pause:hover:after{background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%2218px%22 height%3D%2220px%22 viewBox%3D%220 0 18 20%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3E%E9%A2%84%E8%A7%88%E6%92%AD%E6%94%BE%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Cg id%3D%22Artboard-2%22 transform%3D%22translate(-446.000000%2C -115.000000)%22 fill%3D%22%2323bdd9%22%3E            %3Cg id%3D%22Group-12%22 transform%3D%22translate(446.000000%2C 115.000000)%22%3E                %3Cpath d%3D%22M17.5258914%2C10.7953904 L1.4484631%2C19.8724989 C0.990863649%2C20.1308537 0.401755017%2C19.9841502 0.132652138%2C19.5448276 C0.0457979589%2C19.4030342 0%2C19.2415294 0%2C19.0770349 L3.41490118e-15%2C0.922817934 C3.41490118e-15%2C0.413159662 0.430348031%2C0 0.961209231%2C0 C1.1325471%2C0 1.30077079%2C0.0439687597 1.4484631%2C0.127353928 L17.5258914%2C9.20446242 C17.9834908%2C9.46281716 18.1362975%2C10.0283964 17.8671946%2C10.4677191 C17.7843081%2C10.603035 17.6668367%2C10.7158145 17.5258914%2C10.7953904 Z%22 id%3D%22Triangle-2%22%3E%3C%2Fpath%3E            %3C%2Fg%3E        %3C%2Fg%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 23px;background-color:#fff}.ct-video-view .bottom{background:#f6f6f6;bottom:0;display:flex;height:40px;padding:0 16px;position:absolute;transition:opacity .5s ease;width:100%}.ct-video-view .bottom:hover{opacity:1}.ct-video-view .bottom.hidden{opacity:0}.ct-video-view .play-btn{cursor:pointer;height:18px;margin-top:11px;transition:all .1s ease;width:18px}.ct-video-view .play-btn:hover path{fill:#3d91f2}.ct-video-view .progress-bar{background:#d9d9d9;background-clip:content-box;border-radius:100px;cursor:pointer;flex:1;height:10px;margin:16px 16px 0;padding:3px 0;position:relative}.ct-video-view .load-bar{background:#b6b6b6;transition:all 3s linear;z-index:1}.ct-video-view .load-bar,.ct-video-view .played-bar{border-radius:100px;height:4px;position:absolute;top:3px;width:0}.ct-video-view .played-bar{background:#1c6cdc;transition:all .3s linear;z-index:2}.ct-video-view .played-bar.dragged{transition:none}.ct-video-view .played-button{background:#fff;border-radius:50%;box-shadow:0 1px 2px 0 rgba(0,0,0,.2);height:12px;position:absolute;top:-2px;transition:all .3s linear;width:12px;z-index:10}.ct-video-view .played-button.dragged{transition:none}.ct-video-view .progress-tip-wrapper{height:28px;left:0;position:absolute;top:-27px;width:48px}.ct-video-view .progress-tip{background:rgba(0,0,0,.68);border-radius:2px;color:#fff;font-size:12px;height:25px;left:50%;line-height:25px;padding:0 10px;position:absolute;top:0;transform:translate(-50%);vertical-align:middle;white-space:nowrap;z-index:9}.ct-video-view .progress-tip:after{border-color:rgba(0,0,0,.68) transparent;border-style:solid;border-width:4px 4px 0;bottom:-4px;width:0}.ct-video-view .progress-tip:after,.ct-video-view .progress-tip:before{content:"";display:block;position:absolute;right:50%;transform:translate(50%);z-index:2}.ct-video-view .progress-tip:before{background:rgba(0,0,0,.68);bottom:-9px;height:4px;width:1px}.ct-video-view .time{display:flex;justify-content:space-between;align-items:center;color:#333;font-size:12px;line-height:12px;text-align:right;width:80px}.ct-video-view .volume-btn{cursor:pointer;height:40px;margin:0 16px;padding-top:10px;position:relative;transition:all .1s ease;width:20px}.ct-video-view .volume-btn:hover path:first-child{fill:#3d91f2}.ct-video-view .volume-btn:hover path:nth-child(2){stroke:#3d91f2}.ct-video-view .volume-bar-wrapper{background:#fff;border-radius:2px;box-shadow:0 4px 16px 0 rgba(106,93,73,.2),0 8px 20px 0 rgba(106,93,73,.1);height:102px;left:-15%;position:absolute;top:-102px;width:30px;z-index:9}.ct-video-view .volume-bar{background:#d9d9d9;border-radius:100px;height:80px;left:50%;position:absolute;top:11px;transform:translate(-50%);width:4px}.ct-video-view .selected-bar{background:#1c6cdc;border-radius:100px;bottom:0;height:0;position:absolute;transition:all .2s;width:4px;z-index:2}.ct-video-view .selected-bar .bar-btn{background:#fff;border-radius:50%;box-shadow:0 1px 2px 0 rgba(0,0,0,.2);content:"";height:12px;position:absolute;right:-4px;top:-6px;width:12px}.ct-video-view .scale-btn{cursor:pointer;height:20px;margin-right:16px;margin-top:10px;transition:all .1s ease;width:20px}.ct-video-view .scale-btn:hover path{fill:#3d91f2}.ct-video-view .download-btn{cursor:pointer;height:24px;margin-top:10px;position:relative;transition:all .1s ease;width:25px}.ct-video-view .download-btn:hover path{fill:#3d91f2}.ct-video-view .download-btn .tip-wrapper{display:none;height:28px;left:7px;position:absolute;top:-30px}.ct-video-view .download-btn .tip{background:rgba(0,0,0,.68);border-radius:2px;color:#fff;font-size:12px;height:25px;left:50%;line-height:25px;padding:0 10px;position:absolute;top:0;transform:translate(-50%);vertical-align:middle;white-space:nowrap;z-index:9}.ct-video-view .download-btn .tip:after{border-color:rgba(0,0,0,.68) transparent;border-style:solid;border-width:4px 4px 0;bottom:-4px;content:"";display:block;position:absolute;right:50%;transform:translate(50%);width:0;z-index:2}.ct-video-view .download-btn.video-loading:after{-webkit-animation:loading .8s linear infinite;animation:loading .8s linear infinite;border-radius:50%;border:2px solid #d5d5d5;content:"";display:inline-block;height:18px;vertical-align:-8%;width:18px}.ct-video-view .download-btn.video-loading svg{display:none}.ct-video-view .download-btn.error{background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 encoding%3D%22UTF-8%22%3F%3E%3Csvg width%3D%228px%22 height%3D%228px%22 viewBox%3D%220 0 8 8%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E    %3C!-- Generator%3A Sketch 50.2 (55047) - http%3A%2F%2Fwww.bohemiancoding.com%2Fsketch --%3E    %3Ctitle%3ECombined Shape%3C%2Ftitle%3E    %3Cdesc%3ECreated with Sketch.%3C%2Fdesc%3E    %3Cdefs%3E%3C%2Fdefs%3E    %3Cg id%3D%22Page-1%22 stroke%3D%22none%22 stroke-width%3D%221%22 fill%3D%22none%22 fill-rule%3D%22evenodd%22%3E        %3Crect id%3D%22Rectangle-2%22 fill%3D%22%23FFFFFF%22 x%3D%222%22 y%3D%221%22 width%3D%224%22 height%3D%226%22%3E%3C%2Frect%3E        %3Cpath d%3D%22M0%2C4 C0%2C1.79088 1.79088%2C0 4%2C0 C6.20912%2C0 8%2C1.79088 8%2C4 C8%2C6.20912 6.20912%2C8 4%2C8 C1.79088%2C8 0%2C6.20912 0%2C4 Z M4.00000002%2C1.34999998 C3.34188252%2C1.34999998 2.87727375%2C1.67743076 3%2C2.5 L3.40000002%2C4.36638153 C3.40000002%2C4.69277782 3.66862917%2C4.96459961 4.00000002%2C4.96459961 C4.33368113%2C4.96459961 4.60000002%2C4.69676825 4.60000002%2C4.36638153 L5%2C2.5 C5.15197984%2C1.72235314 4.74297343%2C1.34999998 4.00000002%2C1.34999998 Z M4%2C6.65000002 C4.33137086%2C6.65000002 4.60000002%2C6.38137086 4.60000002%2C6.05 C4.60000002%2C5.71862914 4.33137086%2C5.44999998 4%2C5.44999998 C3.66862914%2C5.44999998 3.39999998%2C5.71862914 3.39999998%2C6.05 C3.39999998%2C6.38137086 3.66862914%2C6.65000002 4%2C6.65000002 Z%22 id%3D%22Combined-Shape%22 fill%3D%22%23F5222D%22%3E%3C%2Fpath%3E    %3C%2Fg%3E%3C%2Fsvg%3E") no-repeat 100% 0}.ct-video-view .download-btn.error:hover .tip-wrapper{display:block}.ct-video-view:-moz-full-screen{height:100%;width:100%}.ct-video-view:-moz-full-screen hover{border:none}.ct-video-view:-moz-full-screen .middle.pause:after{transform:scale(2)}.ct-video-view:-moz-full-screen .bottom{bottom:40px;left:50%;margin-left:-256px;width:512px}.ct-video-view:full-screen{height:100%;width:100%}.ct-video-view:full-screen hover{border:none}.ct-video-view:full-screen .middle.pause:after{transform:scale(2)}.ct-video-view:full-screen .bottom{bottom:40px;left:50%;margin-left:-256px;width:512px}.ct-video-view:-webkit-full-screen{height:100%;width:100%}.ct-video-view:-ms-fullscreen{height:100%;width:100%}.ct-video-view:fullscreen{height:100%;width:100%}.ct-video-view:-webkit-full-screen hover{border:none}.ct-video-view:-ms-fullscreen hover{border:none}.ct-video-view:fullscreen hover{border:none}.ct-video-view:-webkit-full-screen .middle.pause:after{transform:scale(2)}.ct-video-view:-ms-fullscreen .middle.pause:after{transform:scale(2)}.ct-video-view:fullscreen .middle.pause:after{transform:scale(2)}.ct-video-view:-webkit-full-screen .bottom{bottom:40px;left:50%;margin-left:-256px;width:512px}.ct-video-view:-ms-fullscreen .bottom{bottom:40px;left:50%;margin-left:-256px;width:512px}.ct-video-view:fullscreen .bottom{bottom:40px;left:50%;margin-left:-256px;width:512px}.ProseMirror-selectednode .ct-video-view{border:1px solid #1c6cdc}.ct-video-view.error{background:rgba(0,0,0,.5);font-size:14px}.ct-video-view.error .notice{color:#fff;margin-top:100px;text-align:center}.ct-video-view.error .retry{background:#1c6cdc;border:none;color:#fff;display:block;height:32px;line-height:32px;margin:24px auto 0;width:76px}.ct-video-view.mobile{border:1px solid #ddd;max-width:100%;min-width:0}.ct-video-view.mobile video{margin:0}';
styleInject(css_248z$1);

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
var VideoView = function(props) {
    var attrs = props.attrs, provider = props.provider, view = props.view, getPos = props.getPos;
    var width = attrs.width, height = attrs.height;
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames("".concat(classNamePrefix("video")), {
            "align-center": attrs.align === "center",
            "align-right": attrs.align === "right"
        })
    }, /*#__PURE__*/ React.createElement(Video, _extends({}, attrs, {
        width: width,
        height: height,
        provider: provider,
        view: view,
        getPos: getPos
    })));
};

var img$7 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='24px' height='20px' viewBox='0 0 24 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 50.2 (55047) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3eGroup 3%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='Artboard-3' transform='translate(-86.000000%2c -30.000000)' fill='%23BDC2C7'%3e %3cg id='Group-3' transform='translate(86.000000%2c 30.000000)'%3e %3cpath d='M1%2c0 L23%2c0 C23.5522847%2c-1.01453063e-16 24%2c0.44771525 24%2c1 L24%2c19 C24%2c19.5522847 23.5522847%2c20 23%2c20 L1%2c20 C0.44771525%2c20 6.76353751e-17%2c19.5522847 0%2c19 L0%2c1 C-6.76353751e-17%2c0.44771525 0.44771525%2c1.01453063e-16 1%2c0 Z M2%2c2 L2%2c18 L22%2c18 L22%2c2 L2%2c2 Z' id='Combined-Shape'%3e%3c/path%3e %3cpath d='M4%2c6 L2%2c6 L2%2c4 L4%2c4 L4%2c2 L6%2c2 L6%2c18 L4%2c18 L4%2c16 L2%2c16 L2%2c14 L4%2c14 L4%2c11 L2%2c11 L2%2c9 L4%2c9 L4%2c6 Z M20%2c11 L20%2c14 L22%2c14 L22%2c16 L20%2c16 L20%2c18 L18%2c18 L18%2c2 L20%2c2 L20%2c4 L22%2c4 L22%2c6 L20%2c6 L20%2c9 L22%2c9 L22%2c11 L20%2c11 Z M9.7906191%2c5.56472793 L15.4303866%2c9.59313326 C15.6550929%2c9.75363776 15.7071387%2c10.0659128 15.5466342%2c10.2906191 C15.5145644%2c10.3355169 15.4752843%2c10.3747969 15.4303866%2c10.4068667 L9.7906191%2c14.4352721 C9.5659128%2c14.5957766 9.25363776%2c14.5437307 9.09313326%2c14.3190244 C9.03256157%2c14.2342241 9%2c14.1326168 9%2c14.0284053 L9%2c5.97159466 C9%2c5.69545229 9.22385763%2c5.47159466 9.5%2c5.47159466 C9.60421149%2c5.47159466 9.70581872%2c5.50415623 9.7906191%2c5.56472793 Z' id='Combined-Shape'%3e%3c/path%3e %3c/g%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var img$8 = "data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e%3csvg width='16px' height='16px' viewBox='0 0 16 16' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e %3c!-- Generator: Sketch 45.1 (43504) - http://www.bohemiancoding.com/sketch --%3e %3ctitle%3etost-%e9%94%99%e8%af%af%3c/title%3e %3cdesc%3eCreated with Sketch.%3c/desc%3e %3cdefs%3e%3c/defs%3e %3cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e %3cg id='Artboard' transform='translate(-296.000000%2c -544.000000)' fill='%23F05E50'%3e %3cpath fill='%23F05E50' d='M304%2c544 C308.41824%2c544 312%2c547.58176 312%2c552 C312%2c556.41824 308.41824%2c560 304%2c560 C299.58176%2c560 296%2c556.41824 296%2c552 C296%2c547.58176 299.58176%2c544 304%2c544 Z M299.2%2c552 C299.2%2c552.667362 299.735663%2c553.2 300.396436%2c553.2 L307.603564%2c553.2 C308.256356%2c553.2 308.8%2c552.662742 308.8%2c552 C308.8%2c551.332638 308.264337%2c550.8 307.603564%2c550.8 L300.396436%2c550.8 C299.743644%2c550.8 299.2%2c551.337258 299.2%2c552 Z' id='tost-%e9%94%99%e8%af%af'%3e%3c/path%3e %3c/g%3e %3c/g%3e%3c/svg%3e";

var css_248z$2 = ".selected .ct-upload-video{border:1px solid #118bfb}.ct-upload-video{background-color:#f6f6f6;border:1px solid #ddd;color:unset;display:block;height:288px;outline:none;text-align:left;text-decoration:none;transition:all .1s ease;width:512px}.ct-upload-video:active{border:1px solid #1253b5}.ct-upload-video:hover{border:1px solid #3d91f2}.ct-upload-video .icon{margin-top:85px;text-align:center}.ct-upload-video .name{-webkit-box-orient:vertical;-webkit-line-clamp:1;color:#333;display:-webkit-box;font-size:12px;line-height:18px;margin:22px auto;overflow:hidden;text-align:center;-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none;width:400px}.ct-upload-video .info{display:flex;justify-content:space-between;margin:0 auto;width:200px}.ct-upload-video .progress-container{text-align:center}.ct-upload-video .progress-wrapper{background-color:hsla(0,0%,86.7%,0);border-radius:6px;border:1px solid #ddd;display:inline-block;height:8px;position:relative;width:200px}.ct-upload-video .progress{background-color:#1253b5;border-radius:6px;border:1px solid #1253b5;box-shadow:0 2px 8px 0 rgba(71,134,255,.24);height:8px;left:0;position:absolute;top:-1px}.ct-upload-video .percent,.ct-upload-video .size{color:#999;display:inline-block;font-size:12px;line-height:12px}.ct-upload-video .percent,.ct-upload-video .reupload,.ct-upload-video .size{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.ct-upload-video .reupload{font-size:14px;display:flex;justify-content:center}.ct-upload-video .reupload svg{vertical-align:-12%}.ct-upload-video .reupload .retry-btn{margin-left:5px;cursor:pointer;color:#40a9ff}";
styleInject(css_248z$2);

function _assertThisInitialized$1(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _classCallCheck$2(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _getPrototypeOf$1(o) {
    _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$1(o);
}
function _inherits$1(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$1(subClass, superClass);
}
function _possibleConstructorReturn$1(self, call) {
    if (call && (_typeof$1(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$1(self);
}
function _setPrototypeOf$1(o, p) {
    _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$1(o, p);
}
var _typeof$1 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$1() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$1(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$1();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$1(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$1(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$1(this, result);
    };
}
var __generator$1 = window && window.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return(g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g);
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
var VideoPlaceholder = /*#__PURE__*/ function(Component) {
    _inherits$1(VideoPlaceholder, Component);
    var _super = _createSuper$1(VideoPlaceholder);
    function VideoPlaceholder() {
        _classCallCheck$2(this, VideoPlaceholder);
        var _this;
        _this = _super.apply(this, arguments);
        _this.state = {
            progress: 0,
            hasError: false,
            loaded: "0MB",
            uploadDone: false,
            transcodeDone: false
        };
        _this.onRetry = null;
        // 修改进度
        _this.setProgress = function(progress) {
            _this.setState({
                progress: progress
            });
        };
        // 修改错误状态
        _this.setHasError = function(hasError) {
            _this.setState({
                hasError: hasError
            });
        };
        // 修改已加载大小
        _this.setLoaded = function(loaded) {
            _this.setState({
                loaded: loaded
            });
        };
        var _this1 = _assertThisInitialized$1(_this);
        // 修改上传状态
        _this.setUploadDone = function() {
            var _ref = _asyncToGenerator$1(function(uploadDone) {
                return __generator$1(this, function(_state) {
                    _this1.setState({
                        uploadDone: uploadDone
                    });
                    return [
                        2
                    ];
                });
            });
            return function(uploadDone) {
                return _ref.apply(this, arguments);
            };
        }();
        // 修改转码状态
        _this.setTranscodeDone = function(transcodeDone) {
            _this.setState({
                transcodeDone: transcodeDone
            });
        };
        // 重新上传
        _this.retry = function() {
            var onRetry = _assertThisInitialized$1(_this).onRetry;
            _this.setState({
                hasError: false
            }, function() {
                return onRetry && onRetry();
            });
        };
        return _this;
    }
    var _proto = VideoPlaceholder.prototype;
    _proto.render = function render() {
        var _this_props = this.props, _this_props_name = _this_props.name, name = _this_props_name === void 0 ? i18nClient.t("pk_untitled", "未命名") : _this_props_name, size = _this_props.size;
        var _this_state = this.state, progress = _this_state.progress, loaded = _this_state.loaded, hasError = _this_state.hasError, uploadDone = _this_state.uploadDone, transcodeDone = _this_state.transcodeDone;
        var fakeDone = loaded === size;
        return /*#__PURE__*/ React.createElement("div", {
            className: classNamePrefix("upload-video")
        }, /*#__PURE__*/ React.createElement("div", {
            className: "icon"
        }, /*#__PURE__*/ React.createElement("img", {
            src: img$7,
            alt: i18nClient.t("pk_video", "视频")
        })), /*#__PURE__*/ React.createElement("div", {
            className: "name"
        }, name), !hasError && /*#__PURE__*/ React.createElement("div", {
            className: "info"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "size"
        }, formatFileSize(loaded), " / ", size), !transcodeDone && /*#__PURE__*/ React.createElement("span", {
            className: "percent"
        }, fakeDone && size ? "99%" : "".concat(progress, "%"))), !hasError && /*#__PURE__*/ React.createElement("div", {
            className: "progress-container"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "progress-wrapper"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "progress",
            style: {
                width: "".concat(progress, "%")
            }
        }))), !hasError && uploadDone && !transcodeDone && /*#__PURE__*/ React.createElement("div", {
            className: "name"
        }, i18nClient.t("video_upload_tip", "视频正在转码中，如文件较大可能时间较长，请耐心等待…")), hasError && /*#__PURE__*/ React.createElement("div", {
            className: "reupload"
        }, /*#__PURE__*/ React.createElement("img", {
            src: img$8,
            alt: i18nClient.t("pk_upload_fail", "上传失败")
        }), /*#__PURE__*/ React.createElement("span", null, i18nClient.t("pk_upload_fail", "上传失败"), "，"), /*#__PURE__*/ React.createElement("span", {
            className: "retry-btn",
            onClick: this.retry
        }, i18nClient.t("pk_retry", "重试"))));
    };
    return VideoPlaceholder;
}(Component);

/**
 * 创建上传视频占位区域
 *
 */ var createPlaceholder = function(idMap) {
    return function(param, id) {
        var file = param.file;
        var widget = document.createElement("div");
        widget.setAttribute("data-type", "ct-progress");
        var name = file.name;
        // 渲染组件，并将组件的实例存入 idMap 中，以 id 为 key
        ReactDOM.render(/*#__PURE__*/ React.createElement(VideoPlaceholder, {
            id: id,
            name: name,
            file: file,
            size: formatFileSize(file.size),
            ref: function(ref) {
                idMap[id] = ref;
            }
        }), widget);
        return widget;
    };
};

function _assertThisInitialized$2(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _classCallCheck$3(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _getPrototypeOf$2(o) {
    _getPrototypeOf$2 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf$2(o);
}
function _inherits$2(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf$2(subClass, superClass);
}
function _possibleConstructorReturn$2(self, call) {
    if (call && (_typeof$2(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized$2(self);
}
function _setPrototypeOf$2(o, p) {
    _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf$2(o, p);
}
var _typeof$2 = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct$2() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper$2(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct$2();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf$2(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf$2(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn$2(this, result);
    };
}
var __generator$2 = window && window.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return(g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g);
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
var idMap = {};
var VideoExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits$2(VideoExtension, NodeExtension);
    var _super = _createSuper$2(VideoExtension);
    function VideoExtension() {
        _classCallCheck$3(this, VideoExtension);
        var _this;
        _this = _super.apply(this, arguments);
        _this.nodeViews = VideoView;
        return _this;
    }
    var _proto = VideoExtension.prototype;
    _proto.plugin = function plugin() {
        return new Plugin(UploaderOption({
            key: pluginKey
        }, {
            createUploadMask: createPlaceholder(idMap)
        }));
    };
    _proto.commands = function commands(param) {
        var getState = param.getState, providers = param.providers, editorEventEmitter = param.editorEventEmitter;
        var _this = this;
        return {
            isDisabled: function() {
                return function() {
                    return !canInsertTypes(getState());
                };
            },
            upload: function() {
                return function(state, dispatch, view) {
                    var provider = providers.get(_this.name);
                    selectFile({
                        accept: "video/*",
                        multiple: true
                    }).then(function() {
                        var _ref = _asyncToGenerator$2(function(files) {
                            var _tmp, len, i;
                            return __generator$2(this, function(_state) {
                                switch(_state.label){
                                    case 0:
                                        _tmp = provider.onSelectFiles;
                                        if (!_tmp) return [
                                            3,
                                            2
                                        ];
                                        return [
                                            4,
                                            provider.onSelectFiles(files)
                                        ];
                                    case 1:
                                        _tmp = !_state.sent();
                                        _state.label = 2;
                                    case 2:
                                        if (_tmp) {
                                            view.focus();
                                            return [
                                                2
                                            ];
                                        }
                                        len = files && files.length;
                                        if (len > 0) {
                                            for(i = 0; i < len; i++){
                                                onUpload({
                                                    view: view,
                                                    provider: provider,
                                                    idMap: idMap,
                                                    eventEmitter: editorEventEmitter
                                                })(files.item(i));
                                            }
                                        }
                                        return [
                                            2
                                        ];
                                }
                            });
                        });
                        return function(files) {
                            return _ref.apply(this, arguments);
                        };
                    }());
                    return true;
                };
            }
        };
    };
    _createClass(VideoExtension, [
        {
            key: "name",
            get: function get() {
                return "video";
            }
        }
    ]);
    return VideoExtension;
}(NodeExtension);

export { VideoExtension };
//# sourceMappingURL=index.js.map
